/**
 * ConversationalOnboarding
 *
 * UI for the TENANT_INITIAL flow. One signed-up user completes it for the tenant
 * (`single_user` / `flow_level`). The conversational screens are internal; the
 * backend tracks five steps (see `src/constants/onboarding.ts`):
 *
 * 1. PROPERTY
 * 2. SPACES
 * 3. ASSETS
 * 4. JOBS
 * 5. COMPLETE — extra step so advancing to Jobs does not auto-complete
 *    (backend completes when `currentStep >= totalSteps`)
 *
 * Advance with `advanceTenantOnboardingStep`. Complete only from the Finish
 * action via `completeTenantOnboardingFlow`.
 *
 * @see docs/onboarding/ONBOARDING_FRONTEND_GUIDE.md
 */
import { faPen } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatInTimeZone } from 'date-fns-tz';
import { motion } from 'framer-motion';
import { nanoid } from 'nanoid';
import pluralize from 'pluralize';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router-dom';
import { toast } from 'react-toastify';
import { gql, useMutation, useQuery } from 'urql';

import { useAppContext } from '~/App';
import { useBrandName } from '~/brand/BrandContext';
import { client } from '~/client';
import { Address } from '~/components/ui/Address';
import { StatusBadge } from '~/components/ui/StatusBadge';
import { OnboardingFlowKey, getTotalSteps } from '~/constants/onboarding';
import { type IndustryName } from '~/constants/onboardingPresets';
import {
  CompleteTenantOnboardingFlowDocument,
  CreateContactDocument,
  CreateSiteDocument,
  JobStatus,
  StartTenantOnboardingFlowDocument,
  UpdateAccountProfileDocument,
  useCreateAssetMutation,
  useCreateDeploymentMutation,
  useCreateSpaceMutation,
  useUpdateAssetMutation,
  useUpdateSpaceMutation,
} from '~/generated/graphql';
import { graphql } from '~/gql';
import { CreateAutomationDocument, Status } from '~/gql/graphql';
import { getTenantTimezone } from '~/helpers/timezoneUtils';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import HousePlaceholderImg from '~/images/images/HousePlaceholder.webp';
import { useContacts } from '~/routes/resources/contacts';
import { useSites } from '~/routes/resources/sites';
import { useSiteQuery } from '~/routes/sites.$siteId';

// Extracted modules
import { useAssets } from '~/components/AssetSelect';
import { storeCreatedJobDates } from '~/helpers/jobCreationHints';
import { AssetCardSelector, SelectedAsset } from './AssetCardSelector';
import { ChatBubble, SystemMessage } from './chat';
import { useChatMessages, useOnboardingScroll } from './hooks';
import { PlaceTypeCardSelector } from './PlaceTypeCardSelector';
import {
  AssetInfo,
  RecurringJobConfig,
  RecurringJobSetup,
} from './RecurringJobSetup';
import {
  buildContactInput,
  buildContinueOnboardingJobInput,
  buildFirstAssetJobInput,
  buildProfileJobInput,
  buildRecurringAssetAutomationInput,
  buildRecurringAssetJobInput,
  buildRecurringAssetTemplateInput,
  buildSiteInput,
  clearOnboardingResume,
  extractGraphQLErrorMessage,
  formatAssetNamesForDisplay,
  getOnboardingResume,
  markOnboardingComplete,
  saveOnboardingJobIds,
  uploadPlaceholderImage,
  type OnboardingResumeStep,
  type SeededTemplateTask,
} from './services';
import { findJobSuggestionChecklist } from './findJobSuggestionChecklist';
import { findOnboardingMatchingTemplate } from './onboardingTemplateMatch';
import { SpaceCardSelector } from './SpaceCardSelector';
import { AddressStep, FinishStep, IndustryStep, PlaceNameStep } from './steps';
import { WelcomeScreen } from './WelcomeScreen';
import {
  mapBackendOnboardingStep,
  mapJobResumeStep,
  shouldApplyReloadResume,
} from './onboardingResumeSteps';

// =============================================================================
// GRAPHQL DOCUMENTS
// =============================================================================

const CreateJobsDocument = graphql(`
  mutation CreateJobs($input: CreateJobInput!) {
    createJobs(input: $input) {
      id
      status
      name
      createdAt
      location {
        id
      }
      scheduledStartDate
      scheduledEndDate
    }
  }
`);

// These are the same mutations used in CreateJobForm.tsx
// Defined locally to work with urql's useMutation hook
const CreateJobTemplateDocument = graphql(`
  mutation CreateJobTemplate($input: JobTemplateInput!) {
    createJobTemplate(input: $input) {
      id
    }
  }
`);

const AdvanceTenantOnboardingStepDocument = graphql(`
  mutation AdvanceTenantOnboardingStep($flowKey: String!, $totalSteps: Int!) {
    advanceTenantOnboardingStep(flowKey: $flowKey, totalSteps: $totalSteps) {
      flowKey
      currentStep
      completedAt
      completedByUserId
      __typename
    }
  }
`);

const OnboardingListJobTemplatesDocument = graphql(`
  query OnboardingListJobTemplates($status: Status) {
    jobTemplates(status: $status) {
      id
      name
      isStarter
      status
      tasks {
        id
        type
        name
        description
        attributes {
          id
          type
          category
          name
          value
        }
        jobAttributeIds
        tags {
          nodes {
            id
            entityType
            name
            category
            description
            colour
          }
        }
        config {
          selection
          spaces
          assetTypes
          allow
          shareAttributes
          shareJobHistory
          shareDeploymentHistory
        }
        assets {
          id
          name
          type
        }
        attribute {
          id
          attributes
        }
      }
    }
  }
`);

// =============================================================================
// TYPES
// =============================================================================

/**
 * Onboarding steps:
 * 0: Welcome screen
 * 1: Industry selection
 * 2: Place name
 * 3: Place type (NEW)
 * 4: Address
 * 5: Space selection
 * 6: Asset creation
 * 7: Recurring job setup
 * 8: Finish
 */
type OnboardingStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8;

// =============================================================================
// PROGRESS CONFIGURATION
// =============================================================================

const PROGRESS_VALUES: Record<OnboardingStep, number> = {
  0: 0,
  1: 0.1,
  2: 0.18,
  3: 0.26,
  4: 0.38,
  5: 0.52,
  6: 0.66,
  7: 0.82,
  8: 1,
};

const PROGRESS_LABELS: Record<OnboardingStep, string> = {
  0: '',
  1: 'Getting started...',
  2: 'Setting up your first location...',
  3: 'Choosing your place type...',
  4: 'Adding location details...',
  5: 'Adding spaces...',
  6: 'Adding your first asset...',
  7: 'Setting up recurring jobs...',
  8: '',
};

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function ConversationalOnboarding() {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { tenant, user } = useAppContext();
  const { isMobile } = useBreakpoint();
  const brandName = useBrandName();

  // Check if user is resuming via localStorage (Continue Setup from job CTA)
  // This needs to be checked BEFORE completedFlow to allow users to resume
  // even if the backend incorrectly marked the flow as complete
  const hasResumeData = useMemo(() => {
    try {
      return !!window.localStorage.getItem('onboardingResume');
    } catch {
      return false;
    }
  }, []);

  // Check if onboarding is already completed
  // BUT: If user has resume data, don't show completion screen - they want to continue
  const completedFlow = hasResumeData
    ? null
    : tenant?.onboardingFlows?.find(
        (flow) =>
          flow.flowKey === OnboardingFlowKey.TENANT_INITIAL && flow.completedAt
      );

  // Contact resolution
  const contacts = useContacts({ includeInactive: false });
  const currentUserContact = contacts.find((contact) => contact.isCurrentUser);
  const currentUserContactId =
    currentUserContact?.id ?? tenant?.contact_id ?? null;

  // Mutations
  const [, createSite] = useMutation(CreateSiteDocument);
  const [, createJobs] = useMutation(CreateJobsDocument);
  const [, createContact] = useMutation(CreateContactDocument);
  const [, createSpace] = useCreateSpaceMutation();
  const [, updateSpace] = useUpdateSpaceMutation();
  const [, createAsset] = useCreateAssetMutation();
  const [, updateAsset] = useUpdateAssetMutation();
  const [, createDeployment] = useCreateDeploymentMutation();
  const [, startOnboardingFlow] = useMutation(
    StartTenantOnboardingFlowDocument
  );
  const [, completeOnboardingFlow] = useMutation(
    CompleteTenantOnboardingFlowDocument
  );
  const [, advanceOnboardingStep] = useMutation(
    AdvanceTenantOnboardingStepDocument
  );
  const [, updateAccountProfile] = useMutation(UpdateAccountProfileDocument);
  const [, createJobTemplate] = useMutation(CreateJobTemplateDocument);
  const [, createAutomation] = useMutation(CreateAutomationDocument);

  // Core state
  const [step, setStep] = useState<OnboardingStep>(0);
  const [industry, setIndustry] = useState<string | null>(null);
  const [placeName, setPlaceName] = useState('');
  const [placeType, setPlaceType] = useState<string | null>(null);
  const [address, setAddress] = useState<unknown>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [createdSiteId, setCreatedSiteId] = useState<string | null>(null);

  // Query site data when we have a siteId (for resume - reuses existing Site query)
  const [{ data: siteData, fetching: siteDataFetching }] = useSiteQuery(
    createdSiteId ?? ''
  );

  // Query all sites for the tenant (used for page reload resume)
  const sites = useSites();

  // Query all assets for the tenant (used for page reload resume)
  const [allAssets, , assetsFetching] = useAssets();

  // Query spaces for the onboarding site (used for page reload resume)
  const [{ data: spacesData }] = useQuery<{
    location: { spaces: { id: string; name: string; image?: string | null }[] };
  }>({
    query: gql`
      query SpacesByLocation($id: ID!) {
        location(id: $id) {
          id
          spaces {
            id
            name
            image
          }
        }
      }
    `,
    variables: { id: createdSiteId ? `Site:${createdSiteId}` : '' },
    pause: !createdSiteId,
    requestPolicy: 'cache-and-network', // Always fetch fresh data to ensure spaces are up-to-date
  });

  // Query job templates to find templates for copying task content
  const [{ data: templatesData }] = useQuery({
    query: OnboardingListJobTemplatesDocument,
    requestPolicy: 'cache-and-network', // Ensure fresh data is available
  });

  // Helper to fetch fresh templates imperatively (for use in async functions)
  const fetchFreshTemplates = async () => {
    const result = await client
      .query(
        OnboardingListJobTemplatesDocument,
        {},
        { requestPolicy: 'network-only' }
      )
      .toPromise();
    // Check for errors and return predictable shape
    if (result.error) {
      console.error('Error fetching fresh templates:', result.error);
      return { jobTemplates: [] };
    }
    return result.data ?? { jobTemplates: [] };
  };

  // Track if user is resuming onboarding (clicked "Continue Setup" from a job)
  // When resuming, we should NOT recreate the "Complete my profile" job since it was already created
  const [isResuming, setIsResuming] = useState(false);

  // Space state (Step 4)
  const [selectedSpaces, setSelectedSpaces] = useState<string[]>([]);
  const [customSpaces, setCustomSpaces] = useState<string[]>([]);
  // Map of space name -> space ID for tracking created spaces (used to detect deselection on back)
  const [createdSpaceMap, setCreatedSpaceMap] = useState<
    Record<string, string>
  >({});

  // Asset state (Step 5) - supports multiple assets
  const [selectedAssets, setSelectedAssets] = useState<SelectedAsset[]>([]);
  // Track previously selected assets (for detecting deselection on back)
  const [previouslySelectedAssets, setPreviouslySelectedAssets] = useState<
    SelectedAsset[]
  >([]);

  // Recurring job state (Step 6) - supports multiple configs (one per asset)
  const [recurringJobConfigs, setRecurringJobConfigs] = useState<
    RecurringJobConfig[]
  >([]);

  // UI state
  const [showAddressSection, setShowAddressSection] = useState(false);
  const [isAddressSectionFadingOut, setIsAddressSectionFadingOut] =
    useState(false);
  const [showFinishButton, setShowFinishButton] = useState(false);
  const [editingPlaceName, setEditingPlaceName] = useState<string | null>(null);
  const [placeNameMessageId, setPlaceNameMessageId] = useState<string | null>(
    null
  );

  // Refs - must be declared before useOnboardingScroll since it needs composerRef
  const composerRef = useRef<HTMLDivElement | null>(null);

  // Custom hooks
  const { messages, setMessages, say, addMessage } = useChatMessages();
  const {
    chatEndRef,
    chatContainerRef,
    scrollToBottomIfAtBottom, // Only scrolls if user is at bottom (per guidelines)
    scrollToElementWithDelay, // For user-initiated scroll (e.g., focus on input)
    isAtBottom,
  } = useOnboardingScroll({ composerRef });

  // Refs
  const hasWelcomedRef = useRef(false);
  const hasResumedRef = useRef(false);
  const placeNameMessageRef = useRef<HTMLDivElement | null>(null);
  const addressSectionRef = useRef<HTMLDivElement | null>(null);
  const addressInputRef = useRef<HTMLDivElement | null>(null);
  // Note: composerRef is declared before useOnboardingScroll hook since it's passed as an option
  const isTransitioningToLargeComposerRef = useRef(false);
  const siteCardMessageIdRef = useRef<string | null>(null);
  const mainContainerRef = useRef<HTMLDivElement | null>(null);

  // NOTE: With flex column layout, we no longer need to track composer height
  // for padding. The flex layout naturally keeps the composer at the bottom
  // and the scroll area takes remaining space. This avoids mobile Safari
  // position:fixed + keyboard issues.

  // Computed values
  const progress = PROGRESS_VALUES[step];
  const progressLabel = PROGRESS_LABELS[step];

  // Use tenant industry when resuming, otherwise use stored industry
  const currentIndustry = useMemo(
    () =>
      isResuming
        ? tenant?.industry || 'Other Industry'
        : industry || 'Other Industry',
    [isResuming, tenant?.industry, industry]
  );

  // Check if any assets have space assignments that need to be reviewed
  // This happens when spaces are removed and assets were assigned to them
  const assetsNeedingSpaceReassignment = selectedAssets.filter(
    (asset) =>
      asset.spaces.some((space) => space !== undefined) === false &&
      previouslySelectedAssets.some(
        (prev) =>
          prev.name === asset.name && prev.spaces.some((s) => s !== undefined)
      )
  );

  // Track if user started onboarding in this session (prevents reload resume from triggering mid-flow)
  const hasStartedInSessionRef = useRef(false);

  // ==========================================================================
  // RESUME STATE CHECK
  // ==========================================================================
  // Check for resume state on mount. This handles two scenarios:
  // 1. User clicked "Continue Setup" from a job (localStorage resume)
  // 2. User reloaded page during active onboarding (backend step check)
  //
  // IMPORTANT: This effect should only show welcome messages ONCE per resume.
  // The hasResumedRef prevents duplicate messages when dependencies change.
  //
  // CRITICAL: The reload resume logic should ONLY trigger on actual page reload,
  // not when the user is actively progressing through onboarding in the same session.
  // hasStartedInSessionRef tracks this to prevent false triggers when backend step updates.
  useEffect(() => {
    // Skip if we've already handled resume
    if (hasResumedRef.current) return;

    // First, check for job-based resume (highest priority)
    const resume = getOnboardingResume();
    if (resume) {
      // Clear the resume state immediately to prevent re-triggering
      clearOnboardingResume();

      // Mark that we've handled resume - prevents duplicate messages
      hasResumedRef.current = true;

      // Mark that we're resuming - this prevents recreating the "Complete my profile" job
      // which was already created when the user first skipped onboarding
      setIsResuming(true);

      // Restore state from resume
      setCreatedSiteId(resume.siteId);
      setPlaceName(resume.placeName);
      setIndustry(resume.industry);

      const targetStep = mapJobResumeStep(resume.step);
      setStep(targetStep);

      // Show appropriate welcome message for resumed state - match directive style of other steps
      // NOTE: Use resume.placeName directly since state hasn't updated yet
      const resumeMessages: Record<OnboardingResumeStep, React.ReactNode> = {
        spaces: (
          <div key='m-resume-spaces'>
            👋 Welcome back! Let&apos;s set up the spaces inside &quot;
            {resume.placeName}&quot;.
            <div className='mt-1 text-xs text-white/80'>
              Spaces are areas like rooms or zones where your assets live.
            </div>
          </div>
        ),
        assets: (
          <div key='m-resume-assets'>
            👋 Welcome back! Let&apos;s add your assets for &quot;
            {resume.placeName}&quot;.
            <div className='mt-1 text-xs text-white/80'>
              Assets are things that need regular maintenance like appliances,
              equipment, or vehicles. You can add as many as you like!
            </div>
          </div>
        ),
        jobs: (
          <div key='m-resume-jobs'>
            👋 Welcome back! Would you like to set up recurring maintenance jobs
            for your assets?
          </div>
        ),
      };

      say([resumeMessages[resume.step]], { simulateTyping: false });
      return; // Don't check for page reload resume if we have job resume
    }

    // Second, check for active onboarding flow (page reload scenario)
    // CRITICAL: Only trigger this on actual page reload, NOT during active session
    // If user has started onboarding in this session, skip reload resume logic
    const activeFlow = tenant?.onboardingFlows?.find(
      (flow) =>
        flow.flowKey === OnboardingFlowKey.TENANT_INITIAL && !flow.completedAt
    );

    if (
      activeFlow &&
      shouldApplyReloadResume({
        hasStartedInSession: hasStartedInSessionRef.current,
        currentStep: activeFlow.currentStep,
        hasSites: sites.length > 0,
      })
    ) {
      // User was in the middle of onboarding when they reloaded
      // We need to restore their progress based on the backend step

      hasResumedRef.current = true;

      // Find the site created during onboarding
      const onboardingSite = sites[0];

      if (onboardingSite) {
        setCreatedSiteId(onboardingSite.id);
        setPlaceName(onboardingSite.name);
        setPlaceType(onboardingSite.type || null);

        // Try to restore address if available
        if (onboardingSite.address) {
          try {
            const addressData =
              typeof onboardingSite.address === 'string'
                ? JSON.parse(onboardingSite.address)
                : onboardingSite.address;
            setAddress(addressData);
          } catch {
            setAddress(onboardingSite.address);
          }
        }

        // Restore industry from tenant
        if (tenant?.industry) {
          setIndustry(tenant.industry);
        }

        setIsResuming(true);

        const backendStep = activeFlow.currentStep;
        if (backendStep == null) {
          return;
        }

        const targetStep = mapBackendOnboardingStep(backendStep);
        setStep(targetStep);

        // Show step-specific welcome message for page reload resume
        const stepMessages: Record<number, React.ReactNode> = {
          2: (
            <div key='m-reload-spaces'>
              👋 Welcome back! Let&apos;s continue setting up the spaces inside
              &quot;{onboardingSite.name}&quot;.
              <div className='mt-1 text-xs text-white/80'>
                Spaces are areas like rooms or zones where your assets live.
              </div>
            </div>
          ),
          3: (
            <div key='m-reload-assets'>
              👋 Welcome back! Let&apos;s continue adding your assets for &quot;
              {onboardingSite.name}&quot;.
              <div className='mt-1 text-xs text-white/80'>
                Assets are things that need regular maintenance like appliances,
                equipment, or vehicles. You can add as many as you like!
              </div>
            </div>
          ),
          4: (
            <div key='m-reload-jobs'>
              👋 Welcome back! Let&apos;s continue setting up recurring
              maintenance jobs for your assets.
            </div>
          ),
        };

        const resumeMessage = stepMessages[backendStep];
        if (resumeMessage) {
          say([resumeMessage], { simulateTyping: false });
        }
      } else {
        console.warn(
          '[ONBOARDING] Could not find onboarding site for active flow'
        );
        // Reset to step 0 if we can't restore state
        setStep(0);
      }
    }
    // Note: getOnboardingResume/clearOnboardingResume are not included in deps
    // as they are pure functions with no reactive state.
    // placeName is NOT in deps because we use onboardingSite.name directly in the messages.
  }, [
    say,
    tenant?.onboardingFlows,
    tenant?.industry,
    sites,
    allAssets,
    spacesData,
  ]);

  useEffect(() => {
    if (siteData?.site?.type && isResuming) {
      // When resuming, always use the site type (which is the place type)
      setPlaceType(siteData.site.type);
    }
  }, [siteData?.site?.type, isResuming, placeType]);

  // Populate selectedSpaces when resuming and spacesData loads
  useEffect(() => {
    // Only run when resuming and spaces data is available
    if (!isResuming || !spacesData?.location?.spaces) return;

    // Only populate if selectedSpaces is empty (not already set)
    // This prevents overwriting user selections when going back/forward
    if (selectedSpaces.length > 0) return;

    const siteSpaces = spacesData.location.spaces;
    if (siteSpaces.length > 0) {
      const spaceNames = siteSpaces.map((space) => space.name);
      setSelectedSpaces(spaceNames);
    }
    // Note: selectedSpaces is intentionally not in deps to avoid re-running
    // when user changes selection. We only want to run this once on resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResuming, spacesData?.location?.spaces]);

  // Populate selectedAssets when resuming and allAssets loads
  useEffect(() => {
    // Only run when resuming and we have a site ID and assets data
    if (!isResuming || !createdSiteId || allAssets.length === 0) return;

    // Only populate if selectedAssets is empty (not already set)
    // This prevents overwriting user selections when going back/forward
    if (selectedAssets.length > 0) return;

    // Filter assets by the onboarding site
    const siteAssets = allAssets.filter(
      (asset) => asset.deployment?.location?.id === createdSiteId
    );

    if (siteAssets.length > 0) {
      // Group assets by base name to reconstruct SelectedAsset format
      const assetGroups = new Map<
        string,
        { assets: typeof siteAssets; type: string }
      >();

      siteAssets.forEach((asset) => {
        // Remove quantity suffix like " #2", " #3" to get base name
        const baseName = asset.name.replace(/\s+#\d+$/, '');
        const existing = assetGroups.get(baseName) || {
          assets: [],
          type: asset.type,
        };
        existing.assets.push(asset);
        assetGroups.set(baseName, existing);
      });

      // Convert to SelectedAsset format with space assignments
      const restoredAssets: SelectedAsset[] = Array.from(
        assetGroups.entries()
      ).map(([name, { assets, type }]) => {
        // Try to determine space assignments from deployment data
        const spaceAssignments: (string | undefined)[] = [];
        assets.forEach((asset, index) => {
          const deploymentSpace = asset.deployment?.space?.name;
          spaceAssignments[index] = deploymentSpace;
        });

        return {
          name,
          type,
          quantity: assets.length,
          spaces: spaceAssignments,
          ids: assets.map((a) => a.id),
          isDeployed: true, // Assume deployed since they exist
        };
      });

      setSelectedAssets(restoredAssets);
      setPreviouslySelectedAssets(restoredAssets);
    }
    // Note: selectedAssets is intentionally not in deps to avoid re-running
    // when user changes selection. We only want to run this once on resume.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isResuming, createdSiteId, allAssets]);

  // When step changes, composer content changes significantly
  // Wait for layout recalculation before scrolling
  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollToBottomIfAtBottom();
      });
    });
  }, [
    step,
    progressLabel,
    messages.length,
    isAtBottom,
    scrollToBottomIfAtBottom,
  ]);

  // ==========================================================================
  // SCROLL EFFECTS
  // ==========================================================================
  // Implements chat-behaviour-guidelines.md:
  // - "Effective bottom" = top edge of composer (section 1.3)
  // - Auto-scroll only when user is at bottom (section 3)
  // - Preserve reading position when user scrolls up (section 4)
  // - When composer changes height, adjust if user was at bottom (section 7)

  /**
   * Effect: Handle new messages
   * Per guidelines section 3.1: Auto-scroll when user is at bottom
   */
  useEffect(() => {
    scrollToBottomIfAtBottom();
  }, [messages.length, scrollToBottomIfAtBottom, isAtBottom, step]);

  /**
   * Track previous composer height to detect changes
   */
  const prevComposerHeightRef = useRef<number>(0);

  /**
   * Effect: Handle composer height changes
   * Per guidelines section 7.2-7.3:
   * - When composer grows AND user was at bottom → scroll to keep last message visible
   * - When composer grows AND user was NOT at bottom → preserve position (do nothing)
   */
  useEffect(() => {
    if (!composerRef.current) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const newHeight = entry.contentRect.height;
      const prevHeight = prevComposerHeightRef.current;

      // Skip initial measurement
      if (prevHeight === 0) {
        prevComposerHeightRef.current = newHeight;

        return;
      }

      // Skip tiny changes
      if (Math.abs(newHeight - prevHeight) < 5) return;

      prevComposerHeightRef.current = newHeight;

      // Per guidelines section 7.2-7.3:
      // When composer height changes, scroll to bottom ONLY if user was at bottom
      // This keeps last message visible above the new composer size
      // Use RAF to wait for layout recalculation after composer height change
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToBottomIfAtBottom();
        });
      });
    });

    observer.observe(composerRef.current);
    return () => observer.disconnect();
    // Note: composerRef is a ref and doesn't need to be in the dependency array - refs are stable across renders
  }, [scrollToBottomIfAtBottom, isAtBottom, step]);

  /**
   * Effect: Handle address section appearing
   * The AddressStep renders AFTER a 250ms delay (showAddressSection changes to true)
   * We need to trigger a scroll when it appears since it adds content to the scroll area
   */
  useEffect(() => {
    if (!showAddressSection || step !== 4) return;

    // Wait for the AddressStep to render and layout to complete
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Try scrolling to the address section element if it exists
        // This ensures the address input is visible even if technically "at bottom"
        if (addressSectionRef.current) {
          scrollToElementWithDelay(addressSectionRef.current, 0);
        } else {
          scrollToBottomIfAtBottom();
        }
      });
    });
  }, [
    showAddressSection,
    step,
    scrollToBottomIfAtBottom,
    scrollToElementWithDelay,
  ]);

  // ==========================================================================
  // ONBOARDING STEP ADVANCEMENT
  // ==========================================================================

  /**
   * Advance the onboarding flow to the next step
   * The backend automatically increments currentStep when this is called.
   * NOTE: The flow should NEVER be automatically completed by advancing steps.
   * Completion should only happen when the user explicitly finishes the last step.
   */
  async function advanceOnboardingStepTo() {
    const flowKey = OnboardingFlowKey.TENANT_INITIAL;
    const totalSteps = getTotalSteps(flowKey);

    // Check if flow is already completed - if so, don't try to advance
    const existingFlow = tenant?.onboardingFlows?.find(
      (flow) => flow.flowKey === flowKey
    );
    if (existingFlow?.completedAt) {
      console.warn(
        '[ONBOARDING] Flow is already completed, skipping advancement'
      );
      return;
    }

    try {
      const result = await advanceOnboardingStep({
        flowKey,
        totalSteps,
      });

      if (result.error) {
        console.error('[ONBOARDING] Error advancing step:', result.error);
        const errorMessage = extractGraphQLErrorMessage(result.error);
        toast.error(
          errorMessage || 'Failed to save progress. Please try again.'
        );
      }
    } catch (error) {
      console.error('[ONBOARDING] Failed to advance step:', error);
      toast.error('Failed to save progress. Please try again.');
    }
  }

  // ==========================================================================
  // CONTACT HELPER
  // ==========================================================================

  async function ensureCurrentUserContact(): Promise<string | null> {
    if (currentUserContactId) return currentUserContactId;

    if (!user || !user.email) {
      console.error('Cannot create contact: user information missing');
      return null;
    }

    try {
      // Convert user to match UserContext type (phone can be null in user, but undefined in UserContext)
      const userContext = {
        name: user.name,
        email: user.email,
        phone: user.phone ?? undefined,
      };
      const contactInput = buildContactInput(userContext);
      const result = await createContact({ input: contactInput });

      if (result.error) {
        console.error('Failed to create contact:', result.error);
        return null;
      }

      return result.data?.createContact?.id ?? null;
    } catch (error) {
      console.error('Error creating contact:', error);
      return null;
    }
  }

  // ==========================================================================
  // INITIAL WELCOME MESSAGE
  // ==========================================================================

  useEffect(() => {
    if (step !== 1 || hasWelcomedRef.current) return;
    hasWelcomedRef.current = true;
    say(
      [
        <span key='m1'>
          👋 Welcome to {brandName} — we&apos;ll get you set up in under a
          minute.
        </span>,
        <div key='m2'>
          <div>First, what is your industry?</div>
        </div>,
      ],
      { simulateTyping: false }
    );
  }, [step, say, brandName]);

  // ==========================================================================
  // PLACE NAME EDITING
  // ==========================================================================

  function renderPlaceNameContent(name: string, isEditing: boolean) {
    if (isEditing) {
      return (
        <div className='flex w-full flex-col gap-2'>
          <input
            className='border-grey-80/30 placeholder-grey-60 focus:ring-grey-80 w-full rounded border bg-white px-2 py-1 text-sm text-grey-90 focus:outline-none focus:ring-1'
            placeholder='Type your property name...'
            value={placeName}
            onChange={(e) => setPlaceName(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === 'Enter' && placeName.trim().length > 0) {
                await handlePlaceNameSubmit();
              }
              if (e.key === 'Escape') {
                setPlaceName(editingPlaceName || '');
                setEditingPlaceName(null);
              }
            }}
            autoFocus={!isMobile}
          />
          <div className='flex items-center gap-1.5'>
            <button
              type='button'
              className='hover:bg-grey-80 rounded bg-grey-90 px-2 py-1 text-xs text-white'
              onClick={handlePlaceNameSubmit}
              disabled={!placeName.trim()}
            >
              Update
            </button>
            <button
              type='button'
              className='text-grey-80 rounded px-2 py-1 text-xs hover:text-grey-90'
              onClick={() => {
                setPlaceName(editingPlaceName || '');
                setEditingPlaceName(null);
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className='flex items-center gap-2'>
        <span key='u-place'>{name}</span>
        <button
          type='button'
          className='text-grey-70 hover:text-grey-90'
          onClick={() => {
            setEditingPlaceName(name);
            setPlaceName(name);
          }}
          aria-label='Edit place name'
        >
          <FontAwesomeIcon icon={faPen} className='h-3 w-3' />
        </button>
      </div>
    );
  }

  // ==========================================================================
  // HANDLERS
  // ==========================================================================

  async function handleIndustrySubmit(selectedIndustry: IndustryName) {
    setIndustry(selectedIndustry);

    addMessage('user', selectedIndustry);
    scrollToBottomIfAtBottom();
    setStep(2);

    // Update account profile in the background - don't block onboarding flow if it fails
    // This is a non-critical operation, so we silently handle errors
    // Note: If this fails, onboarding will continue - the profile can be updated later
    updateAccountProfile({
      input: {
        name: tenant?.name || 'My Organization',
        industry: selectedIndustry,
      },
    })
      .then((result) => {
        if (result.error) {
          // Log error but don't show to user - profile update is non-critical for onboarding
          // The onboarding flow will continue regardless
          const errorMessage = extractGraphQLErrorMessage(result.error);
          console.warn(
            '[ONBOARDING] Failed to update account profile (non-blocking, continuing onboarding):',
            errorMessage || result.error.message,
            result.error
          );
        }
      })
      .catch((err) => {
        // Catch any unexpected errors - this shouldn't happen with urql but just in case
        console.warn(
          '[ONBOARDING] Unexpected error updating account profile (non-blocking, continuing onboarding):',
          err
        );
      });

    await say(
      [<div key='m3'>Great. What&apos;s the name of your property?</div>],
      { simulateTyping: true }
    );
  }

  async function handlePlaceNameSubmit() {
    const trimmed = placeName.trim();
    if (!trimmed) return;

    // If message ID exists (from previous submission or going back), update it
    // Otherwise create a new message
    if (placeNameMessageId) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === placeNameMessageId
            ? { ...m, content: renderPlaceNameContent(trimmed, false) }
            : m
        )
      );
      setEditingPlaceName(null);
    } else {
      const messageId = nanoid();
      setPlaceNameMessageId(messageId);
      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          from: 'user',
          content: renderPlaceNameContent(trimmed, false),
        },
      ]);
    }

    if (step === 2) {
      setStep(3);
      await say(
        [
          <div key='m6'>
            Great! What type of place is &quot;{trimmed}&quot;?
          </div>,
        ],
        { simulateTyping: true }
      );
    }
  }

  /**
   * Handle place type selection - auto-advances to address step
   */
  async function handlePlaceTypeSelect(type: string) {
    setPlaceType(type);

    addMessage('user', type);
    scrollToBottomIfAtBottom();

    setStep(4);
    setShowAddressSection(false);
    await say(
      [
        <div key='m7'>
          Nice! 🏠 Now what&apos;s the address?
          <div className='mt-1 text-xs text-white/80'>
            Start typing and choose from suggestions if you like.
          </div>
        </div>,
      ],
      { simulateTyping: false }
    );
    await new Promise((r) => setTimeout(r, 250));
    setShowAddressSection(true);
  }

  /**
   * Go back to welcome screen from industry step
   */
  function handleBackToWelcome() {
    hasWelcomedRef.current = false; // Reset so welcome message shows again
    hasResumedRef.current = false; // Reset resume state
    setMessages([]); // Clear messages
    setStep(0);
  }

  /**
   * Go back to industry step from place name step
   */
  function handleBackToIndustry() {
    // Remove the last user message (industry selection) and bot messages after it
    setMessages((prev) => {
      // Find the industry selection message (first user message)
      const firstUserIndex = prev.findIndex((m) => m.from === 'user');
      if (firstUserIndex >= 0) {
        return prev.slice(0, firstUserIndex);
      }
      return prev;
    });
    setIndustry(null);
    // Clear place name state since user will need to re-enter after selecting industry
    setPlaceName('');
    setPlaceNameMessageId(null);
    setStep(1);
  }

  /**
   * Go back to place name step from place type step
   */
  function handleBackToPlaceName() {
    // Remove messages from step 3 onward (keep industry + place name)
    // The place name message should already be shown
    setMessages((prev) => {
      // Find the place name message and keep everything up to it
      if (placeNameMessageId) {
        const placeNameIndex = prev.findIndex(
          (m) => m.id === placeNameMessageId
        );
        if (placeNameIndex >= 0) {
          // Keep up to and including place name message
          return prev.slice(0, placeNameIndex + 1);
        }
      }
      return prev;
    });
    setPlaceType(null);
    setStep(2);
  }

  /**
   * Go back to place type step from address step
   */
  function handleBackToPlaceType() {
    setShowAddressSection(false);
    setStep(3);
  }

  /**
   * Handle address submission - creates the site and moves to spaces step
   */
  async function handleAddressSubmit() {
    if (!placeName) return;

    let lastErrorMessage: string | null = null;
    setIsSubmitting(true);

    try {
      setIsAddressSectionFadingOut(true);

      if (address) {
        addMessage(
          'user',
          <span key='u-address'>
            <Address
              data={
                typeof address === 'string' ? address : JSON.stringify(address)
              }
            />
          </span>
        );
        scrollToBottomIfAtBottom();
      }

      await new Promise((r) => setTimeout(r, 300));
      setShowAddressSection(false);
      setIsAddressSectionFadingOut(false);

      // Upload placeholder image and create site
      const uploadedImageId = await uploadPlaceholderImage();
      const siteInput = buildSiteInput(
        placeName,
        address,
        uploadedImageId,
        placeType
      );
      const siteRes = await createSite({ input: siteInput });

      if (siteRes.error) {
        console.error('CreateSite GraphQL Error:', {
          message: siteRes.error.message,
          graphQLErrors: siteRes.error.graphQLErrors,
          networkError: siteRes.error.networkError,
        });
      }

      const siteId = siteRes.data?.createSite?.id;
      if (!siteId) {
        lastErrorMessage =
          extractGraphQLErrorMessage(siteRes.error) ??
          'We could not create your place just now.';
        throw new Error(lastErrorMessage);
      }

      setCreatedSiteId(siteId);

      const newSite = siteRes.data?.createSite;

      // Show success message
      await say(
        [<span key='m3'>You successfully added your first place. 🎉</span>],
        { simulateTyping: false, typingMs: 500 }
      );

      await new Promise((r) => setTimeout(r, 350));

      // Show site card
      if (newSite) {
        const siteCardMessageId = addMessage(
          'system',
          <SiteCard
            name={newSite.name}
            addressData={
              newSite.address ??
              (address
                ? typeof address === 'string'
                  ? address
                  : JSON.stringify(address)
                : null)
            }
          />
        );
        siteCardMessageIdRef.current = siteCardMessageId;
      }

      // Scroll to show the site card if user is at bottom
      await new Promise((r) => setTimeout(r, 100));
      scrollToBottomIfAtBottom();

      // Wait a bit more before transitioning to step 4
      await new Promise((r) => setTimeout(r, 400));

      // Prevent scroll-to-bottom during transition to step 4
      isTransitioningToLargeComposerRef.current = true;

      // Transition to spaces step
      await say(
        [
          <div key='m-spaces'>
            Now let&apos;s set up the spaces inside &quot;{placeName}&quot;.
            <div className='mt-1 text-xs text-white/80'>
              Spaces are areas like rooms or zones where your assets live.
            </div>
          </div>,
        ],
        { simulateTyping: true }
      );

      setStep(5);

      // Advance to step 2 (Spaces) after property creation
      await advanceOnboardingStepTo();

      // Clear transition flag and site card ref
      isTransitioningToLargeComposerRef.current = false;
      siteCardMessageIdRef.current = null;

      // ResizeObserver handles scroll when composer size changes
    } catch (e) {
      console.error(e);

      const fallbackMessage =
        'Something went wrong while setting up your first place. Please try again.';
      const userFacingMessage =
        (e instanceof Error && e.message) ||
        lastErrorMessage ||
        fallbackMessage;

      addMessage(
        'bot',
        <div>
          <div>Hmm, something went wrong while setting up your place.</div>
          <div className='mt-1 text-xs text-white/80'>{userFacingMessage}</div>
          <div className='mt-2 text-xs text-white/80'>
            You can adjust the address below and tap <strong>Next</strong> to
            try again.
          </div>
        </div>
      );

      toast.error(userFacingMessage);
      setShowAddressSection(true);
      setIsAddressSectionFadingOut(false);
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * Handle spaces submission - creates new spaces, inactivates deselected spaces, and moves to asset step
   *
   * When user goes back and changes selection:
   * - New spaces (not in createdSpaceMap) are created
   * - Deselected spaces (in createdSpaceMap but not in selectedSpaces) are marked Inactive
   * - Already-created spaces that are still selected are left unchanged
   */
  async function handleSpacesSubmit() {
    if (!createdSiteId) return;

    setIsSubmitting(true);
    const updatedSpaceMap = { ...createdSpaceMap };

    try {
      // Identify spaces to create (selected but not yet created)
      const spacesToCreate = selectedSpaces.filter(
        (name) => !createdSpaceMap[name]
      );

      // Identify spaces to deactivate (previously created but now deselected)
      const spacesToDeactivate = Object.keys(createdSpaceMap).filter(
        (name) => !selectedSpaces.includes(name)
      );

      // Create new spaces
      for (const spaceName of spacesToCreate) {
        const result = await createSpace({
          locationId: `Site:${createdSiteId}`,
          input: {
            name: spaceName,
            images: [],
            status: Status.Active,
          },
        });

        if (result.data?.createSpace?.id) {
          updatedSpaceMap[spaceName] = result.data.createSpace.id;
        }
      }

      // Deactivate removed spaces
      for (const spaceName of spacesToDeactivate) {
        const spaceId = createdSpaceMap[spaceName];
        if (spaceId) {
          const result = await updateSpace({
            id: spaceId,
            input: {
              name: spaceName,
              images: [],
              status: Status.Inactive,
            },
          });
          if (result.error) {
            console.error(
              `Failed to deactivate space "${spaceName}":`,
              result.error
            );
            toast.error(
              `Failed to remove space "${spaceName}". Please try again.`
            );
          } else {
            // Remove from map since it's now inactive
            delete updatedSpaceMap[spaceName];
          }
        }
      }

      setCreatedSpaceMap(updatedSpaceMap);

      // Clean up asset space assignments that reference deactivated spaces
      // This ensures assets referencing removed spaces get their assignments cleared
      let assetsNeedingReassignment = 0;
      if (spacesToDeactivate.length > 0 && selectedAssets.length > 0) {
        const cleanedAssets = selectedAssets.map((asset) => {
          const cleanedSpaces = asset.spaces.map((space) => {
            if (space && spacesToDeactivate.includes(space)) {
              assetsNeedingReassignment++;
              return undefined; // Clear invalid space assignment
            }
            return space;
          });
          return {
            ...asset,
            spaces: cleanedSpaces,
            isDeployed: cleanedSpaces.some((s) => s !== undefined),
          };
        });
        setSelectedAssets(cleanedAssets);
      }

      // Echo what was created/updated
      const createdCount = spacesToCreate.length;
      const deactivatedCount = spacesToDeactivate.length;

      if (createdCount > 0 || deactivatedCount > 0) {
        const parts: string[] = [];
        if (createdCount > 0) {
          parts.push(
            `Added ${createdCount} space${createdCount !== 1 ? 's' : ''}`
          );
        }
        if (deactivatedCount > 0) {
          parts.push(`removed ${deactivatedCount}`);
        }

        addMessage(
          'user',
          <span key='u-spaces'>
            {parts.join(', ')}. Current spaces: {selectedSpaces.join(', ')}
          </span>
        );
        scrollToBottomIfAtBottom();
      } else if (selectedSpaces.length > 0) {
        // First time or no changes
        addMessage(
          'user',
          <span key='u-spaces'>
            Added {selectedSpaces.length} space
            {selectedSpaces.length !== 1 ? 's' : ''}:{' '}
            {selectedSpaces.join(', ')}
          </span>
        );
        scrollToBottomIfAtBottom();
      }

      await new Promise((r) => setTimeout(r, 300));

      // Build appropriate message for asset step
      const assetMessage =
        assetsNeedingReassignment > 0 ? (
          <div key='m-asset'>
            Great! 🛠️ Now let&apos;s review your assets.
            <div className='mt-1 text-xs text-amber-200'>
              ⚠️ Some assets were assigned to spaces that were removed. Please
              reassign them to available spaces.
            </div>
          </div>
        ) : (
          <div key='m-asset'>
            Great! 🛠️ Now let&apos;s add your assets.
            <div className='mt-1 text-xs text-white/80'>
              Assets are things that need regular maintenance like appliances,
              equipment, or vehicles. You can add as many as you like!
            </div>
          </div>
        );

      // Move to asset step
      await say([assetMessage], { simulateTyping: true });

      setStep(6);

      // Advance to step 3 (Assets) after spaces setup
      await advanceOnboardingStepTo();

      // ResizeObserver handles scroll when composer size changes
    } catch (e) {
      console.error('Failed to create/update spaces:', e);
      toast.error('Failed to update spaces. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * Skip spaces step - advance to assets step
   */
  async function handleSpacesSkip() {
    await say(
      [<span key='m-skip-spaces'>No problem! You can add spaces later.</span>],
      { simulateTyping: false }
    );
    // Create "Set up {placeName}" job with CTA to continue onboarding
    // Note: We don't advance the backend step here - App.tsx checks hasStartedOnboarding
    // (currentStep >= 1) to avoid forcing users back to onboarding after skipping
    await handleFinishWithContinuationJob('spaces');
  }

  /**
   * Handle asset submission - creates new assets, inactivates deselected assets, deploys them, and moves to recurring job step
   *
   * When user goes back and changes selection:
   * - New assets (without IDs) are created
   * - Deselected assets (in previouslySelectedAssets but not in selectedAssets) are marked Inactive
   * - Already-created assets that are still selected are left unchanged
   */
  async function handleAssetSubmit() {
    if (!createdSiteId || selectedAssets.length === 0) return;

    setIsSubmitting(true);
    const updatedAssets: SelectedAsset[] = [];

    // Current date in ISO format for deployment startDate
    const deploymentStartDate = new Date().toISOString();

    try {
      // Identify assets to deactivate (previously created but now removed)
      const assetsToDeactivate = previouslySelectedAssets.filter(
        (prevAsset) =>
          prevAsset.ids &&
          prevAsset.ids.length > 0 &&
          !selectedAssets.some((a) => a.name === prevAsset.name)
      );

      // Deactivate removed assets
      for (const asset of assetsToDeactivate) {
        if (asset.ids) {
          for (const assetId of asset.ids) {
            const result = await updateAsset({
              id: assetId,
              input: {
                name: asset.name,
                type: asset.type,
                status: Status.Inactive,
              },
            });
            if (result.error) {
              console.error(
                `Failed to deactivate asset "${asset.name}" (${assetId}):`,
                result.error
              );
              toast.error(
                `Failed to remove asset "${asset.name}". Please try again.`
              );
            }
          }
        }
      }

      // Create assets that don't have IDs yet, or update if quantity changed
      for (const asset of selectedAssets) {
        // Check if this asset was previously created
        const previousAsset = previouslySelectedAssets.find(
          (p) => p.name === asset.name
        );

        // If already created (has IDs) and quantity hasn't changed, keep existing
        if (previousAsset?.ids && previousAsset.ids.length === asset.quantity) {
          updatedAssets.push({
            ...asset,
            ids: previousAsset.ids,
          });
          continue;
        }

        // Need to create new assets (or additional ones if quantity increased)
        const existingIds = previousAsset?.ids || [];
        const assetIdsForType: string[] = [...existingIds];

        // Create additional assets if quantity increased, or all if no existing IDs
        const startIndex = existingIds.length;
        for (let i = startIndex; i < asset.quantity; i++) {
          // Name: "Pool Pump" for single, "Pool Pump #1", "Pool Pump #2" for multiple
          const assetName =
            asset.quantity > 1 ? `${asset.name} #${i + 1}` : asset.name;

          const assetResult = await createAsset({
            input: {
              name: assetName,
              type: asset.type,
              status: Status.Active,
            },
          });

          if (assetResult.error) {
            console.error(
              `Failed to create asset ${assetName}:`,
              assetResult.error
            );
            continue;
          }

          const assetId = assetResult.data?.createAsset?.id;
          if (assetId) {
            assetIdsForType.push(assetId);

            // Deploy the asset to the site with assigned space (or default to first space)
            // asset.spaces[i] corresponds to the space for this specific unit
            const spaceName = asset.spaces[i] || selectedSpaces[0] || 'Main';
            const deployResult = await createDeployment({
              input: {
                assetId,
                locationId: `Site:${createdSiteId}`,
                spaceName,
                startDate: deploymentStartDate,
              },
            });
            if (deployResult.error) {
              console.error(
                `Failed to deploy asset ${assetName}:`,
                deployResult.error
              );
              // Continue - asset was created, deployment is optional
            }
          }
        }

        // If quantity decreased, mark excess assets as inactive
        if (existingIds.length > asset.quantity) {
          const idsToDeactivate = existingIds.slice(asset.quantity);
          for (let i = 0; i < idsToDeactivate.length; i++) {
            const assetId = idsToDeactivate[i];
            const assetName = `${asset.name} #${asset.quantity + i + 1}`;
            const result = await updateAsset({
              id: assetId,
              input: {
                name: assetName,
                type: asset.type,
                status: Status.Inactive,
              },
            });
            if (result.error) {
              console.error(
                `Failed to deactivate asset "${assetName}":`,
                result.error
              );
            }
          }
          // Only keep IDs up to new quantity
          assetIdsForType.splice(asset.quantity);
        }

        // Store ALL asset IDs for job creation (one task per asset)
        updatedAssets.push({
          ...asset,
          ids: assetIdsForType,
        });
      }

      // Update selectedAssets with the created IDs
      setSelectedAssets(updatedAssets);
      setPreviouslySelectedAssets(updatedAssets);

      // Echo what was created - show quantities and space assignments
      const totalAssetCount = selectedAssets.reduce(
        (sum, a) => sum + a.quantity,
        0
      );
      const assetDescriptions = selectedAssets.map((a) => {
        const quantityStr = a.quantity > 1 ? ` × ${a.quantity}` : '';
        const assignedSpaces = a.spaces.filter((s) => s !== undefined);
        const spaceStr =
          assignedSpaces.length > 0 ? ` → ${assignedSpaces.join(', ')}` : '';
        return `${a.name}${quantityStr}${spaceStr}`;
      });

      addMessage(
        'user',
        <span key='u-assets'>
          {totalAssetCount} asset{totalAssetCount !== 1 ? 's' : ''}:{' '}
          {assetDescriptions.join(', ')}
        </span>
      );
      scrollToBottomIfAtBottom();

      await new Promise((r) => setTimeout(r, 300));

      // Move to recurring job step
      await say(
        [
          <div key='m-recurring'>
            Awesome! 📅 Would you like to set up recurring maintenance jobs for
            your assets?
          </div>,
        ],
        { simulateTyping: true }
      );

      setStep(7);

      // Advance to step 4 (Jobs) after assets creation
      await advanceOnboardingStepTo();

      // ResizeObserver handles scroll when composer size changes
    } catch (e) {
      console.error('Failed to create/update assets:', e);
      toast.error('Failed to update assets. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * Skip asset step - advance to jobs step
   */
  async function handleAssetSkip() {
    await say(
      [<span key='m-skip-asset'>No problem! You can add assets later.</span>],
      { simulateTyping: false }
    );
    // Create "Add assets" job with CTA to continue onboarding
    // Note: We don't advance the backend step here - App.tsx checks hasStartedOnboarding
    // (currentStep >= 1) to avoid forcing users back to onboarding after skipping
    await handleFinishWithContinuationJob('assets');
  }

  /**
   * Go back from asset step to spaces step
   */
  function handleBackToSpaces() {
    setStep(5);
    // ResizeObserver handles scroll when composer size changes
  }

  /**
   * Go back from recurring job step to assets step
   */
  function handleBackToAssets() {
    setStep(6);
    // ResizeObserver handles scroll when composer size changes
  }

  /**
   * Handle recurring job completion - called when user creates a recurring job for an asset
   *
   * If config.isForever is true, creates an automation (template + automation).
   * Otherwise, creates a regular job with repeat count.
   *
   * Job/Automation properties:
   * - Location = site (createdSiteId)
   * - Owner = current user
   * - Assignee = no one
   * - Name = jobName from config
   * - Tasks = One Asset task per asset (uses selectedAssetIds if provided, otherwise all IDs)
   */
  async function handleRecurringJobComplete(config: RecurringJobConfig) {
    if (!createdSiteId) return;

    // Determine which asset IDs to use
    let assetIdsToUseRaw: string[];
    let assetNamesFromIds: string[] = [];
    let allAssetsInvolved: typeof selectedAssets = [];

    if (config.selectedAssetIds && config.selectedAssetIds.length > 0) {
      // User selected specific asset IDs - these might be from multiple different assets
      assetIdsToUseRaw = config.selectedAssetIds;

      // Find all assets that these IDs belong to and collect their names
      const assetNameMap = new Map<string, string>(); // assetId -> assetName
      const assetsSet = new Set<(typeof selectedAssets)[0]>();

      config.selectedAssetIds.forEach((assetId) => {
        const asset = selectedAssets.find((a) => a.ids?.includes(assetId));
        if (asset) {
          assetNameMap.set(assetId, asset.name);
          assetsSet.add(asset);
        }
      });

      // Build assetNames array in the same order as assetIdsToUseRaw
      assetNamesFromIds = assetIdsToUseRaw.map(
        (id) => assetNameMap.get(id) || config.assetName
      );
      allAssetsInvolved = Array.from(assetsSet);
    } else {
      // No selectedAssetIds - use all IDs for the primary asset
      const asset = selectedAssets.find((a) => a.name === config.assetName);
      if (!asset?.ids || asset.ids.length === 0) {
        console.error(
          `Cannot create recurring job: Asset "${config.assetName}" not found or missing IDs`
        );
        toast.error(
          `Failed to create job: Asset "${config.assetName}" not found`
        );
        return;
      }
      assetIdsToUseRaw = asset.ids;
      assetNamesFromIds = asset.ids.map(() => config.assetName);
      allAssetsInvolved = [asset];
    }

    // Deduplicate asset IDs to prevent creating duplicate tasks
    // This can happen if asset IDs are accidentally duplicated during collection
    const assetIdsToUse = Array.from(new Set(assetIdsToUseRaw));

    // Build deduplicated assetNames array matching the order of deduplicated IDs
    // Keep the first occurrence of each ID's name
    const seenIds = new Set<string>();
    const deduplicatedAssetNames: string[] = [];
    assetIdsToUse.forEach((id) => {
      if (!seenIds.has(id)) {
        seenIds.add(id);
        const nameIndex = assetIdsToUseRaw.indexOf(id);
        if (nameIndex >= 0 && nameIndex < assetNamesFromIds.length) {
          deduplicatedAssetNames.push(assetNamesFromIds[nameIndex]);
        } else {
          // Fallback if index is out of bounds
          const asset = selectedAssets.find((a) => a.ids?.includes(id));
          deduplicatedAssetNames.push(asset?.name || config.assetName);
        }
      }
    });

    // Check if we have multiple different asset types (different names)
    const uniqueAssetNames = Array.from(new Set(deduplicatedAssetNames));
    const hasMultipleDifferentAssets = uniqueAssetNames.length > 1;

    setIsSubmitting(true);

    try {
      const userContactId = await ensureCurrentUserContact();
      const tenantTimezone = getTenantTimezone(tenant);

      // Update config with proper assetNames for multiple assets
      const configWithAssetNames = {
        ...config,
        assetNames:
          hasMultipleDifferentAssets &&
          deduplicatedAssetNames.length === assetIdsToUse.length
            ? deduplicatedAssetNames
            : config.assetNames,
      };

      // Handle "forever" option - create automation instead of job with repeat
      if (config.isForever) {
        await createAutomationForAsset(
          assetIdsToUse,
          configWithAssetNames,
          tenantTimezone,
          userContactId
        );
      } else {
        // Create regular recurring job with fixed count
        await createRecurringJobForAsset(
          assetIdsToUse,
          configWithAssetNames,
          tenantTimezone,
          userContactId
        );
      }

      // Update state with the completed config
      const updatedConfigs = [...recurringJobConfigs, config];
      setRecurringJobConfigs(updatedConfigs);

      // Show success message - reflect multiple assets when applicable
      const isAutomation = config.isForever;
      const selectedCount = assetIdsToUse.length;

      let assetLabel: string;
      if (hasMultipleDifferentAssets) {
        // Multiple different assets - group by type, pluralize when needed, join with "and"
        assetLabel = formatAssetNamesForDisplay(deduplicatedAssetNames);
      } else {
        // Single asset type (possibly multiple units)
        const totalCount = allAssetsInvolved.reduce(
          (sum, a) => sum + a.quantity,
          0
        );
        if (selectedCount < totalCount) {
          // User selected a subset of units - show count format
          assetLabel = `${selectedCount} of ${totalCount} ${config.assetName}${
            totalCount > 1 ? 's' : ''
          }`;
        } else if (selectedCount > 1) {
          // All units selected - pluralize
          assetLabel = pluralize(config.assetName);
        } else {
          assetLabel = config.assetName;
        }
      }

      addMessage(
        'user',
        <span key={`u-recurring-${config.assetName}`}>
          Created recurring job for {assetLabel}: {config.jobName} (
          {config.freq}
          {isAutomation
            ? ' ongoing'
            : ` ${config.count} job${config.count !== 1 ? 's' : ''}`}
          )
        </span>
      );
      scrollToBottomIfAtBottom();

      await new Promise((r) => setTimeout(r, 300));

      // If this is a "forever" job (automation), explain where to manage it
      if (isAutomation) {
        await say(
          [
            <span key='m-automation-tip'>
              💡 This job will repeat forever. You can adjust the schedule or
              stop it anytime in Settings → Automations.
            </span>,
          ],
          { simulateTyping: false }
        );
        await new Promise((r) => setTimeout(r, 200));
      }

      // Check if all assets now have jobs
      const assetsWithJobs = updatedConfigs.map((c) => c.assetName);
      const assetsWithoutJobs = selectedAssets.filter(
        (a) => !assetsWithJobs.includes(a.name)
      );

      if (assetsWithoutJobs.length === 0) {
        // All assets have jobs - proceed to finish
        await say(
          [
            <span key='m-all-jobs-done'>
              All your assets have recurring jobs configured! 🎉
            </span>,
          ],
          { simulateTyping: false }
        );
        await new Promise((r) => setTimeout(r, 300));
        // Jobs step is complete, proceed to finish (advances backend to step 5)
        await handleFinishWithJobs(false); // Don't create "Add my first asset" job
      }
      // If there are assets without jobs, stay on step 6 - the RecurringJobSetup component
      // will show the option to add another job
    } catch (e) {
      console.error('Error creating recurring job:', e);
      toast.error('Failed to create recurring job. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * Filter template tasks to only include Asset tasks that match selected asset types.
   * Universal Asset tasks (no assetTypes restriction) are transformed to only match unmatched asset types.
   *
   * Rules:
   * - Non-Asset tasks are always included
   * - Asset tasks with assetTypes restriction are included if they match at least one selected asset type
   * - Asset tasks with no assetTypes restriction (universal) are cloned and transformed to only match
   *   unmatched asset types (selectedAssetTypes minus matchedAssetTypes), and only included if unmatchedTypes is non-empty
   */
  function filterTemplateTasksByAssetType(
    tasks: SeededTemplateTask[] | undefined,
    selectedAssetTypes: string[]
  ): SeededTemplateTask[] | undefined {
    if (!tasks) return undefined;

    // First pass: collect which asset types are matched by specific tasks
    const matchedAssetTypes = new Set<string>();
    tasks.forEach((task) => {
      if (task.type === 'Asset') {
        const taskAssetTypes = task.config?.assetTypes;
        // Only count specific tasks (with assetTypes restriction)
        if (taskAssetTypes && taskAssetTypes.length > 0) {
          taskAssetTypes.forEach((taskAssetType) => {
            if (selectedAssetTypes.includes(taskAssetType)) {
              matchedAssetTypes.add(taskAssetType);
            }
          });
        }
      }
    });

    // Compute unmatched asset types (selectedAssetTypes minus matchedAssetTypes)
    const unmatchedTypes = selectedAssetTypes.filter(
      (type) => !matchedAssetTypes.has(type)
    );

    // Second pass: filter and transform tasks
    return tasks.flatMap((task) => {
      // Non-Asset tasks are always included
      if (task.type !== 'Asset') return [task];

      const taskAssetTypes = task.config?.assetTypes;

      // Asset tasks with no assetTypes restriction (universal)
      if (!taskAssetTypes || taskAssetTypes.length === 0) {
        // Only include universal task if there are unmatched asset types
        if (unmatchedTypes.length === 0) return [];

        // Clone and transform the universal task to only match unmatched types
        return [
          {
            ...task,
            config: {
              ...task.config,
              assetTypes: unmatchedTypes,
            },
          },
        ];
      }

      // Asset tasks with assetTypes restriction
      // Include if this task matches at least one selected asset type
      if (
        taskAssetTypes.some((taskAssetType) =>
          selectedAssetTypes.includes(taskAssetType)
        )
      ) {
        return [task];
      }

      return [];
    });
  }

  /**
   * Create a regular recurring job with fixed repeat count
   */
  async function createRecurringJobForAsset(
    assetIds: string[],
    config: RecurringJobConfig,
    tenantTimezone: string,
    userContactId: string | null
  ) {
    if (!createdSiteId) return;

    // Build asset ID to name/type/space mappings for correct task creation
    const assetIdToNameMap: Record<string, string> = {};
    const assetIdToTypeMap: Record<string, string> = {};
    const assetIdToSpaceMap: Record<string, string> = {};

    // Build assetNames array in the same order as assetIds (one name per ID)
    const assetNamesArray: string[] = [];

    assetIds.forEach((assetId) => {
      // Find which asset this ID belongs to
      const asset = selectedAssets.find((a) => a.ids?.includes(assetId));
      if (asset) {
        assetIdToNameMap[assetId] = asset.name;
        assetIdToTypeMap[assetId] = asset.type;
        assetNamesArray.push(asset.name);

        // Find the index of this asset ID to get the corresponding space
        const assetIndex = asset.ids?.indexOf(assetId);
        if (assetIndex !== undefined && assetIndex >= 0) {
          const space = asset.spaces[assetIndex];
          if (space) {
            assetIdToSpaceMap[assetId] = space;
          }
        }
      } else {
        // Fallback if asset not found
        assetNamesArray.push(config.assetName);
      }
    });

    // Get unique asset types from selected assets
    const selectedAssetTypes = Array.from(
      new Set(Object.values(assetIdToTypeMap))
    );

    // Fetch fresh templates to ensure we have latest data, then find matching template
    const freshTemplates = await fetchFreshTemplates();
    const matchingTemplate = findMatchingTemplate(
      config.jobName,
      freshTemplates
    );

    // Filter template tasks to only include Asset tasks that match selected asset types
    // All matching Asset tasks are included (each will create one task per asset ID)
    const filteredTemplateTasks = matchingTemplate?.tasks
      ? filterTemplateTasksByAssetType(
          matchingTemplate.tasks,
          selectedAssetTypes
        )
      : undefined;

    // Determine if we have multiple different assets (different names)
    const uniqueAssetNames = Array.from(new Set(assetNamesArray));
    const hasMultipleDifferentAssets = uniqueAssetNames.length > 1;

    // Build the job input using the service function
    // Pass customUnit for custom frequency normalization (maps 'custom' to actual freq)
    // Pass filtered template tasks to preserve description/checklist content
    // Pass assetNames array when we have multiple different assets and length matches
    const jobInput = buildRecurringAssetJobInput(
      createdSiteId,
      tenantTimezone,
      userContactId,
      {
        assetIds,
        assetName: config.assetName,
        assetNames:
          hasMultipleDifferentAssets &&
          assetNamesArray.length === assetIds.length
            ? assetNamesArray
            : config.assetNames, // Use config.assetNames if provided, otherwise use computed array
        assetIdToNameMap:
          Object.keys(assetIdToNameMap).length > 0
            ? assetIdToNameMap
            : undefined,
        assetIdToTypeMap:
          Object.keys(assetIdToTypeMap).length > 0
            ? assetIdToTypeMap
            : undefined,
        assetIdToSpaceMap:
          Object.keys(assetIdToSpaceMap).length > 0
            ? assetIdToSpaceMap
            : undefined,
        jobName: config.jobName,
        freq: config.freq,
        interval: config.interval,
        byday: config.byday,
        bymonthday: config.bymonthday,
        startDate: config.startDate,
        count: config.count,
        customUnit: config.customUnit,
        seededTemplateTasks: filteredTemplateTasks, // Copy filtered task content from matching template
        // When no backend template matched, use frontend-defined checklist (if any) as task description
        fallbackTaskDescription:
          !filteredTemplateTasks || filteredTemplateTasks.length === 0
            ? findJobSuggestionChecklist(
                config.jobName,
                assetNamesArray,
                selectedAssetTypes,
                industry
              )
            : null,
      }
    );

    // Create the job
    const jobResult = await createJobs({ input: jobInput });

    if (jobResult.error) {
      console.error('Failed to create recurring job:', jobResult.error);
      throw new Error('Failed to create recurring job');
    }

    const createdJobs = jobResult.data?.createJobs;
    if (createdJobs && createdJobs.length > 0) {
      // Store the job ID for potential future use
      saveOnboardingJobIds([createdJobs[0].id]);
      // Store job dates for automatic date filter adjustment when navigating to jobs view
      storeCreatedJobDates(createdJobs);
    }
  }

  function findMatchingTemplate(
    jobName: string,
    templates?: typeof templatesData
  ) {
    return findOnboardingMatchingTemplate(
      jobName,
      templates?.jobTemplates ?? templatesData?.jobTemplates
    );
  }

  /**
   * Create an automation for "forever" recurring jobs.
   * This creates a job template first, then creates an automation that uses it.
   * Always creates a new template (even when based on matching template) to preserve
   * task content (description/checklist) with the specific assets.
   */
  async function createAutomationForAsset(
    assetIds: string[],
    config: RecurringJobConfig,
    tenantTimezone: string,
    userContactId: string | null
  ) {
    if (!createdSiteId) return;

    // Build asset ID to name/type/space mappings for correct task creation
    const assetIdToNameMap: Record<string, string> = {};
    const assetIdToTypeMap: Record<string, string> = {};
    const assetIdToSpaceMap: Record<string, string> = {};

    // Build assetNames array in the same order as assetIds (one name per ID)
    const assetNamesArray: string[] = [];

    assetIds.forEach((assetId) => {
      // Find which asset this ID belongs to
      const asset = selectedAssets.find((a) => a.ids?.includes(assetId));
      if (asset) {
        assetIdToNameMap[assetId] = asset.name;
        assetIdToTypeMap[assetId] = asset.type;
        assetNamesArray.push(asset.name);

        // Find the index of this asset ID to get the corresponding space
        const assetIndex = asset.ids?.indexOf(assetId);
        if (assetIndex !== undefined && assetIndex >= 0) {
          const space = asset.spaces[assetIndex];
          if (space) {
            assetIdToSpaceMap[assetId] = space;
          }
        }
      } else {
        // Fallback if asset not found
        assetNamesArray.push(config.assetName);
      }
    });

    // Get unique asset types from selected assets
    const selectedAssetTypes = Array.from(
      new Set(Object.values(assetIdToTypeMap))
    );

    // Fetch fresh templates to ensure we have latest data, then find matching template
    const freshTemplates = await fetchFreshTemplates();
    const matchingTemplate = findMatchingTemplate(
      config.jobName,
      freshTemplates
    );

    // Filter template tasks to only include Asset tasks that match selected asset types
    // All matching Asset tasks are included (each will create one task per asset ID)
    const filteredTemplateTasks = matchingTemplate?.tasks
      ? filterTemplateTasksByAssetType(
          matchingTemplate.tasks,
          selectedAssetTypes
        )
      : undefined;

    // Helper for duplicate name error detection
    const isDuplicateTemplateNameError = (message?: string | null): boolean => {
      if (!message) return false;
      const lowerMessage = message.toLowerCase();
      return (
        lowerMessage.includes('already exists') ||
        lowerMessage.includes('duplicate') ||
        (lowerMessage.includes('name') &&
          (lowerMessage.includes('taken') ||
            lowerMessage.includes('exists') ||
            lowerMessage.includes('unique')))
      );
    };

    const createTemplateWithRetries = async (
      input: ReturnType<typeof buildRecurringAssetTemplateInput>
    ) => {
      const maxAttempts = 5;
      const baseName = input.name;
      let attemptIndex = 0;
      let lastErrorMessage: string | null = null;
      let createdTemplateId: string | null = null;

      while (attemptIndex < maxAttempts) {
        const nameToUse =
          attemptIndex === 0 ? baseName : `${baseName} (${attemptIndex + 1})`;
        const result = await createJobTemplate({
          input: { ...input, name: nameToUse },
        });

        if (!result.error) {
          createdTemplateId = result.data?.createJobTemplate?.id ?? null;
          if (createdTemplateId) {
            return result;
          }
          // If no ID returned, treat as error and retry
          lastErrorMessage = 'Template created but no ID returned';
          attemptIndex += 1;
          continue;
        }

        const message = result.error?.message ?? null;
        lastErrorMessage = message;

        if (!isDuplicateTemplateNameError(message || '')) {
          // If we already created a template but got a different error, log it
          if (createdTemplateId) {
            console.warn(
              `Template ${createdTemplateId} was created but subsequent operation failed: ${message}`
            );
          }
          throw new Error(
            `Failed to create job template: ${(
              message || 'Unknown error'
            ).replace('[GraphQL] ', '')}`
          );
        }

        attemptIndex += 1;
      }

      throw new Error(
        `Failed to create job template after retrying duplicate names${
          lastErrorMessage
            ? `: ${lastErrorMessage.replace('[GraphQL] ', '')}`
            : ''
        }`
      );
    };

    // Step 1: Create job template
    // Pass customUnit for custom frequency display in notes
    // Pass matching template tasks to preserve description/checklist content
    // Deduplicate asset IDs to ensure no duplicates
    const uniqueAssetIdsForTemplate = Array.from(new Set(assetIds));

    // Build assetNames array matching the order of deduplicated IDs
    // Keep the first occurrence of each ID's name (one name per ID)
    const seenIds = new Set<string>();
    const uniqueAssetNamesForTemplate: string[] = [];
    uniqueAssetIdsForTemplate.forEach((id) => {
      if (!seenIds.has(id)) {
        seenIds.add(id);
        const nameIndex = assetIds.indexOf(id);
        if (nameIndex >= 0 && nameIndex < assetNamesArray.length) {
          uniqueAssetNamesForTemplate.push(assetNamesArray[nameIndex]);
        } else {
          // Fallback if index is out of bounds
          uniqueAssetNamesForTemplate.push(
            assetIdToNameMap[id] || config.assetName
          );
        }
      }
    });

    // Determine if we have multiple different assets (different names)
    const uniqueAssetNames = Array.from(new Set(uniqueAssetNamesForTemplate));
    const hasMultipleDifferentAssets = uniqueAssetNames.length > 1;

    const templateInput = buildRecurringAssetTemplateInput(
      {
        assetIds: uniqueAssetIdsForTemplate,
        assetName: config.assetName,
        assetNames:
          hasMultipleDifferentAssets &&
          uniqueAssetNamesForTemplate.length ===
            uniqueAssetIdsForTemplate.length
            ? uniqueAssetNamesForTemplate
            : config.assetNames, // Use config.assetNames if provided, otherwise use computed array
        assetIdToNameMap:
          Object.keys(assetIdToNameMap).length > 0
            ? assetIdToNameMap
            : undefined,
        assetIdToTypeMap:
          Object.keys(assetIdToTypeMap).length > 0
            ? assetIdToTypeMap
            : undefined,
        assetIdToSpaceMap:
          Object.keys(assetIdToSpaceMap).length > 0
            ? assetIdToSpaceMap
            : undefined,
        jobName: config.jobName,
        siteName: placeName, // Pass property name for template naming
        freq: config.freq,
        interval: config.interval,
        byday: config.byday,
        bymonthday: config.bymonthday,
        startDate: config.startDate,
        customUnit: config.customUnit,
        // When no backend template matched, use frontend-defined checklist (if any) as task description
        fallbackTaskDescription:
          !filteredTemplateTasks || filteredTemplateTasks.length === 0
            ? findJobSuggestionChecklist(
                config.jobName,
                assetNamesArray,
                selectedAssetTypes,
                industry
              )
            : null,
      },
      filteredTemplateTasks // Pass filtered template tasks that match selected asset types
    );

    const templateResult = await createTemplateWithRetries(templateInput);

    if (templateResult.error) {
      console.error('Failed to create job template:', templateResult.error);
      throw new Error('Failed to create job template');
    }

    const createdTemplateId = templateResult.data?.createJobTemplate?.id;
    if (!createdTemplateId) {
      throw new Error('Failed to create job template: no ID returned');
    }

    const templateId = createdTemplateId;

    // Step 2: Create automation using the template
    // Pass customUnit for custom frequency normalization (maps 'custom' to actual freq)
    // Use the same deduplicated asset IDs
    // Determine if we have multiple different assets for automation name
    const uniqueAssetNamesForAutomation = Array.from(
      new Set(uniqueAssetNamesForTemplate)
    );
    const hasMultipleDifferentAssetsForAutomation =
      uniqueAssetNamesForAutomation.length > 1;

    const automationInput = buildRecurringAssetAutomationInput(
      createdSiteId,
      templateId,
      tenantTimezone,
      userContactId,
      {
        assetIds: uniqueAssetIdsForTemplate,
        assetName: config.assetName,
        assetNames:
          hasMultipleDifferentAssetsForAutomation &&
          uniqueAssetNamesForTemplate.length ===
            uniqueAssetIdsForTemplate.length
            ? uniqueAssetNamesForTemplate
            : config.assetNames, // Use config.assetNames if provided, otherwise use computed array
        jobName: config.jobName,
        freq: config.freq,
        interval: config.interval,
        byday: config.byday,
        bymonthday: config.bymonthday,
        startDate: config.startDate,
        customUnit: config.customUnit,
      }
    );

    const automationResult = await createAutomation({ input: automationInput });

    if (automationResult.error) {
      console.error('Failed to create automation:', automationResult.error);
      throw new Error('Failed to create automation');
    }

    const automationId = automationResult.data?.createAutomation;
    if (!automationId) {
      throw new Error('Failed to create automation: no ID returned');
    }

    // Step 3: Create the first job explicitly with the automation ID and tasks
    // This ensures the asset assignments are included in the first job
    // (The automation will handle creating subsequent jobs automatically)
    // Pass matching template tasks to preserve description/checklist content
    // Deduplicate asset IDs again to ensure no duplicates
    const uniqueAssetIdsForJob = Array.from(new Set(assetIds));

    // Build assetNames array matching the order of deduplicated IDs
    // Keep the first occurrence of each ID's name (one name per ID)
    const seenIdsForJob = new Set<string>();
    const uniqueAssetNamesForJob: string[] = [];
    uniqueAssetIdsForJob.forEach((id) => {
      if (!seenIdsForJob.has(id)) {
        seenIdsForJob.add(id);
        const nameIndex = assetIds.indexOf(id);
        if (nameIndex >= 0 && nameIndex < assetNamesArray.length) {
          uniqueAssetNamesForJob.push(assetNamesArray[nameIndex]);
        } else {
          // Fallback if index is out of bounds
          uniqueAssetNamesForJob.push(assetIdToNameMap[id] || config.assetName);
        }
      }
    });

    // Determine if we have multiple different assets (different names)
    const uniqueAssetNamesForJobSet = Array.from(
      new Set(uniqueAssetNamesForJob)
    );
    const hasMultipleDifferentAssetsForJob =
      uniqueAssetNamesForJobSet.length > 1;

    const firstJobInput = buildRecurringAssetJobInput(
      createdSiteId,
      tenantTimezone,
      userContactId,
      {
        assetIds: uniqueAssetIdsForJob,
        assetName: config.assetName,
        assetNames:
          hasMultipleDifferentAssetsForJob &&
          uniqueAssetNamesForJob.length === uniqueAssetIdsForJob.length
            ? uniqueAssetNamesForJob
            : config.assetNames, // Use config.assetNames if provided, otherwise use computed array
        assetIdToNameMap:
          Object.keys(assetIdToNameMap).length > 0
            ? assetIdToNameMap
            : undefined,
        assetIdToTypeMap:
          Object.keys(assetIdToTypeMap).length > 0
            ? assetIdToTypeMap
            : undefined,
        assetIdToSpaceMap:
          Object.keys(assetIdToSpaceMap).length > 0
            ? assetIdToSpaceMap
            : undefined,
        jobName: config.jobName,
        freq: config.freq,
        interval: config.interval,
        byday: config.byday,
        bymonthday: config.bymonthday,
        startDate: config.startDate,
        count: 1, // Only create one job - automation will handle the rest
        customUnit: config.customUnit,
        seededTemplateTasks: filteredTemplateTasks, // Copy filtered task content from matching template
        // When no backend template matched, use frontend-defined checklist (if any) as task description
        fallbackTaskDescription:
          !filteredTemplateTasks || filteredTemplateTasks.length === 0
            ? findJobSuggestionChecklist(
                config.jobName,
                assetNamesArray,
                selectedAssetTypes,
                industry
              )
            : null,
      }
    );

    // Add the automation ID to link this job to the automation
    const firstJobWithAutomation = {
      ...firstJobInput,
      automationId,
    };

    const firstJobResult = await createJobs({ input: firstJobWithAutomation });

    if (firstJobResult.error) {
      console.error(
        'Failed to create first job for automation:',
        firstJobResult.error
      );
      console.error('Job input details:', {
        taskCount: firstJobInput.tasks?.length,
        assetCount: uniqueAssetIdsForJob.length,
        automationId,
        jobName: firstJobInput.name,
      });
      throw new Error(
        `Failed to create first job for automation: ${
          firstJobResult.error.message || 'Unknown error'
        }`
      );
    }

    const createdJobs = firstJobResult.data?.createJobs;
    if (createdJobs && createdJobs.length > 0) {
      saveOnboardingJobIds([createdJobs[0].id]);
      // Store job dates for automatic date filter adjustment when navigating to jobs view
      storeCreatedJobDates(createdJobs);
    }
  }

  /**
   * Skip recurring job step - step 4 is complete, proceed to finish
   */
  async function handleRecurringJobSkip() {
    const numJobs = recurringJobConfigs.length;
    if (numJobs > 0) {
      // User created some jobs but is skipping the rest
      await say(
        [
          <span key='m-skip-partial'>
            {numJobs} recurring job{numJobs !== 1 ? 's' : ''} created. You can
            add more later!
          </span>,
        ],
        { simulateTyping: false }
      );
    } else {
      await say(
        [
          <span key='m-skip-recurring'>
            No problem! You can set up recurring jobs later.
          </span>,
        ],
        { simulateTyping: false }
      );
    }
    // Create "Create jobs for my assets" job with CTA to continue onboarding
    // Note: We don't advance/complete the backend flow here - App.tsx checks hasStartedOnboarding
    // (currentStep >= 1) to avoid forcing users back to onboarding after skipping
    await handleFinishWithContinuationJob('jobs');
  }

  /**
   * Complete onboarding with a continuation job that allows user to resume setup later.
   * Creates:
   * - 'spaces' → "Set up {placeName}" job
   * - 'assets' → "Add assets" job
   * - 'jobs' → "Create jobs for my assets" job
   * - "Complete my profile" job (only if not resuming - when resuming, it was already created)
   *
   * Each job has a CTA link in the description that reopens onboarding at the appropriate step.
   *
   * Note: When user is RESUMING (isResuming=true), we skip creating the "Complete my profile" job
   * because it was already created when they first skipped onboarding.
   */
  async function handleFinishWithContinuationJob(
    resumeStep: OnboardingResumeStep
  ) {
    if (!createdSiteId) return;

    setIsSubmitting(true);

    try {
      const userContactId = await ensureCurrentUserContact();
      const tenantTimezone = getTenantTimezone(tenant);
      const todayLocal = formatInTimeZone(
        new Date(),
        tenantTimezone,
        'yyyy-MM-dd'
      );

      const jobIds: string[] = [];

      // Create continuation job based on skip step
      const continueJobInput = buildContinueOnboardingJobInput(
        createdSiteId,
        tenantTimezone,
        todayLocal,
        userContactId,
        resumeStep,
        placeName,
        currentIndustry
      );
      const continueJobRes = await createJobs({ input: continueJobInput });

      // Check for errors in continuation job creation
      if (continueJobRes.error) {
        console.error(
          '[ONBOARDING] Failed to create continuation job:',
          continueJobRes.error
        );
        const errorMessage = extractGraphQLErrorMessage(continueJobRes.error);
        toast.error(
          errorMessage || 'Failed to create continuation job. Please try again.'
        );
        // Don't throw - continue to try creating profile job
      }

      const continueJobs = continueJobRes.data?.createJobs;
      if (continueJobs && continueJobs.length > 0) {
        jobIds.push(continueJobs[0].id);
        // Store job dates for automatic date filter adjustment when navigating to jobs view
        storeCreatedJobDates(continueJobs);
      }

      // Create "Complete my profile" job ONLY if not resuming
      // When resuming, this job was already created when they first skipped onboarding
      let profileJobId: string | undefined;
      if (!isResuming) {
        const profileJobInput = buildProfileJobInput(
          createdSiteId,
          tenantTimezone,
          todayLocal,
          userContactId
        );
        const profileJobRes = await createJobs({ input: profileJobInput });

        // Check for errors in profile job creation
        if (profileJobRes.error) {
          console.error(
            '[ONBOARDING] Failed to create profile job:',
            profileJobRes.error
          );
          const errorMessage = extractGraphQLErrorMessage(profileJobRes.error);
          toast.error(
            errorMessage || 'Failed to create profile job. Please try again.'
          );
          // Don't throw - continue with whatever jobs were created
        }

        const profileJobs = profileJobRes.data?.createJobs;
        if (profileJobs && profileJobs.length > 0) {
          profileJobId = profileJobs[0].id;
          jobIds.push(profileJobId);
          // Store job dates for automatic date filter adjustment when navigating to jobs view
          storeCreatedJobDates(profileJobs);
        }
      }

      // Save job IDs
      saveOnboardingJobIds(jobIds);

      // NOTE: We do NOT complete the onboarding flow here when skipping.
      // The flow remains incomplete so users can resume it later via the CTA link in the continuation job.
      // The flow will only be completed when they actually finish all steps via handleFinishOnboarding.

      // Get the job title for display
      const jobTitles = {
        spaces: `Set up ${placeName}`,
        assets: 'Add assets',
        jobs: 'Create jobs for my assets',
      };

      // Adjust message based on whether profile job was created
      const showProfileJobMessage = !isResuming;

      await say(
        [
          <div key='m-skip-complete'>
            Great! We&apos;ve created some jobs for you.
            <div className='mt-1 text-sm text-white/80'>
              Click the &quot;Continue Setup&quot; button in your job when
              you&apos;re ready to finish onboarding
              {showProfileJobMessage
                ? ', or complete your profile to personalize your account'
                : ''}
              .
            </div>
          </div>,
        ],
        { simulateTyping: false }
      );

      await new Promise((r) => setTimeout(r, 350));

      // Show continuation job card
      addMessage('system', <JobCard title={jobTitles[resumeStep]} />);
      await new Promise((r) => setTimeout(r, 350));

      // Show profile job card only if it was created
      if (showProfileJobMessage) {
        addMessage(
          'system',
          <JobCard title='Complete my profile' isSecond={true} />
        );
      }

      await new Promise((r) => setTimeout(r, 1000));

      // Navigate to jobs page instead of showing finish step
      // This prevents users from trying to complete the flow when it's not ready
      navigate('/jobs/scheduled');
    } catch (err) {
      console.error('Error creating continuation jobs:', err);
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  /**
   * Complete onboarding and create appropriate jobs
   *
   * Note: When user is RESUMING (isResuming=true), we skip creating the "Complete my profile" job
   * because it was already created when they first skipped onboarding via handleFinishWithContinuationJob.
   */
  async function handleFinishWithJobs(createAssetJob: boolean) {
    if (!createdSiteId) return;

    setIsSubmitting(true);

    try {
      const userContactId = await ensureCurrentUserContact();
      const tenantTimezone = getTenantTimezone(tenant);
      const todayLocal = formatInTimeZone(
        new Date(),
        tenantTimezone,
        'yyyy-MM-dd'
      );

      const jobIds: string[] = [];

      // Conditionally create "Add my first asset" job
      if (createAssetJob) {
        const jobInput = buildFirstAssetJobInput(
          createdSiteId,
          tenantTimezone,
          todayLocal,
          userContactId
        );
        const jobRes = await createJobs({ input: jobInput });
        const createdAssetJobs = jobRes.data?.createJobs;
        if (createdAssetJobs && createdAssetJobs.length > 0) {
          jobIds.push(createdAssetJobs[0].id);
          // Store job dates for automatic date filter adjustment when navigating to jobs view
          storeCreatedJobDates(createdAssetJobs);
        }
      }

      // Create "Complete my profile" job ONLY if not resuming
      // When resuming, this job was already created in handleFinishWithContinuationJob
      if (!isResuming) {
        const profileJobInput = buildProfileJobInput(
          createdSiteId,
          tenantTimezone,
          todayLocal,
          userContactId
        );
        const profileJobRes = await createJobs({ input: profileJobInput });
        const createdProfileJobs = profileJobRes.data?.createJobs;
        if (createdProfileJobs && createdProfileJobs.length > 0) {
          jobIds.push(createdProfileJobs[0].id);
          // Store job dates for automatic date filter adjustment when navigating to jobs view
          storeCreatedJobDates(createdProfileJobs);
        }
      }

      // Save job IDs
      saveOnboardingJobIds(jobIds);

      // Advance to step 5 (COMPLETE) before showing finish UI
      // This ensures the backend knows we've completed the Jobs step
      // and prevents auto-completion (since totalSteps is now 5)
      await advanceOnboardingStepTo();

      // Show success messages
      setStep(8);

      // Adjust message based on whether we're resuming or not
      const showProfileJobMessage = !isResuming;

      await say(
        [
          <div key='m-complete'>
            🎉 You&apos;re all set up!
            {createAssetJob ? (
              <div className='mt-1 text-sm text-white/80'>
                We&apos;ve created jobs to help you add your first asset
                {showProfileJobMessage ? ' and complete your profile' : ''}.
              </div>
            ) : showProfileJobMessage ? (
              <div className='mt-1 text-sm text-white/80'>
                We&apos;ve created a job to help you complete your profile —
                this will make your account feel more personalized, and show you
                what assignees will see when you send them a job.
              </div>
            ) : (
              <div className='mt-1 text-sm text-white/80'>
                Your setup is complete. You can now start managing your
                property!
              </div>
            )}
          </div>,
        ],
        { simulateTyping: false }
      );

      await new Promise((r) => setTimeout(r, 350));

      // Show job cards
      if (createAssetJob) {
        addMessage('system', <JobCard title='Add my first asset' />);
        await new Promise((r) => setTimeout(r, 350));
      }

      // Only show profile job card if we created it (not resuming)
      if (showProfileJobMessage) {
        addMessage(
          'system',
          <JobCard title='Complete your profile' isSecond={createAssetJob} />
        );
      }

      await new Promise((r) => setTimeout(r, 350));
      setShowFinishButton(true);
    } catch (e) {
      console.error('Failed to create jobs:', e);
      toast.error('Failed to complete setup. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleStartOnboarding() {
    try {
      // Mark that user started onboarding in this session
      // This prevents reload resume logic from triggering when step advances
      hasStartedInSessionRef.current = true;

      const startResult = await startOnboardingFlow({
        flowKey: OnboardingFlowKey.TENANT_INITIAL,
      });

      if (startResult.error) {
        console.error('Failed to start onboarding flow:', startResult.error);
      }
      setStep(1);
    } catch (error) {
      console.error('Failed to start onboarding:', error);
      setStep(1);
    }
  }

  /**
   * Complete the onboarding flow.
   *
   * Step 5 (COMPLETE) is the last step. When the user clicks "Finish", we complete the flow.
   * The flow should already be at step 5 (advanced by handleFinishWithJobs), so we use
   * completeTenantOnboardingFlow to explicitly mark it as complete.
   */
  async function handleFinishOnboarding() {
    setIsSubmitting(true);

    try {
      // Validate that the onboarding flow exists and can be completed
      const existingFlow = tenant?.onboardingFlows?.find(
        (flow) => flow.flowKey === OnboardingFlowKey.TENANT_INITIAL
      );

      if (!existingFlow) {
        console.error(
          '[ONBOARDING] No TENANT_INITIAL flow found in tenant onboarding flows'
        );
        throw new Error(
          'Onboarding flow was not properly started. Please refresh and try again.'
        );
      }

      if (existingFlow.completedAt) {
        console.warn(
          '[ONBOARDING] Flow is already completed, navigating to jobs'
        );
        markOnboardingComplete();
        navigate('/jobs/scheduled');
        return;
      }

      // Note: Full flow validation (startedAt, startedByUserId) is handled server-side
      // by the completeTenantOnboardingFlow mutation for single-user flows

      // Complete the flow - step 4 is the last step
      const flowResult = await completeOnboardingFlow({
        flowKey: OnboardingFlowKey.TENANT_INITIAL,
        totalSteps: getTotalSteps(OnboardingFlowKey.TENANT_INITIAL),
      });

      if (flowResult.error) {
        console.error('[ONBOARDING] GraphQL error completing flow:', {
          error: flowResult.error,
          graphQLErrors: flowResult.error.graphQLErrors,
          networkError: flowResult.error.networkError,
          operation: flowResult.operation,
        });

        // Check for specific error types
        const errorMessage = extractGraphQLErrorMessage(flowResult.error);
        console.error('[ONBOARDING] Extracted error message:', errorMessage);

        throw flowResult.error;
      }

      revalidator.revalidate();
    } catch (error) {
      console.error('[ONBOARDING] Failed to complete onboarding:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
      });

      // Show more specific error message to user
      const userMessage =
        extractGraphQLErrorMessage(error) ||
        'Failed to complete setup. Please try again.';
      toast.error(userMessage);
      setIsSubmitting(false);
      return;
    }

    // Only mark as complete if the mutation succeeded
    markOnboardingComplete();

    // Navigate to scheduled jobs page after finishing onboarding
    navigate('/jobs/scheduled');
  }

  /**
   * Handle focus on text inputs - scroll to bottom on mobile when keyboard appears
   * This ensures the input remains visible above the keyboard.
   * Applied to all text input controllers in the onboarding flow.
   */
  function handleInputFocus() {
    if (!isMobile) return;
    // For inputs in the composer (outside scroll container), scroll chat to bottom
    // so user sees latest messages above the composer with keyboard
    scrollToBottomIfAtBottom();
  }

  function handleAddressFocus() {
    if (!isMobile || step !== 4) return;
    scrollToElementWithDelay(addressInputRef.current);
  }

  // ==========================================================================
  // RENDER
  // ==========================================================================

  // If onboarding is completed, show completion message
  if (completedFlow) {
    return (
      <div className='relative mx-auto h-screen w-full overflow-hidden bg-gradient-to-br from-[#00B3B3] to-[#157DA6]'>
        <motion.div
          initial={{ scaleY: 0, opacity: 0 }}
          animate={{ scaleY: 1, opacity: 1 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          style={{ transformOrigin: 'bottom' }}
          className='absolute inset-x-0 bottom-0 top-6 mx-auto flex max-w-2xl flex-col overflow-hidden rounded-t-lg bg-surface-bright md:bottom-6 md:left-4 md:right-4 md:rounded-lg'
        >
          <div className='flex min-h-0 flex-1 flex-col'>
            <div className='flex-1 overflow-y-scroll px-4 py-6 scrollbar-gutter-stable'>
              <div className='mx-auto w-full max-w-2xl'>
                <div className='mb-6 space-y-3 lg:mb-12'>
                  <ChatBubble from='bot'>
                    <div>
                      🎉 You&apos;ve already completed your onboarding setup!
                      <div className='mt-2 text-sm text-white/80'>
                        Your property and maintenance schedules are all
                        configured. You can always manage your assets, jobs, and
                        settings from the main dashboard.
                      </div>
                    </div>
                  </ChatBubble>
                </div>
              </div>
            </div>
          </div>

          <div className='flex max-h-[60vh] min-h-0 flex-shrink-0 flex-col overflow-hidden rounded-b-lg bg-surface-bright px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4'>
            <div className='flex justify-end gap-2'>
              <button
                type='button'
                onClick={() => navigate('/')}
                className='rounded-md bg-brand px-6 py-2.5 text-white hover:bg-brand-dark'
              >
                Go to Dashboard
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  if (step === 0) {
    return <WelcomeScreen onGetStarted={handleStartOnboarding} />;
  }

  return (
    <div className='relative mx-auto h-screen w-full overflow-hidden bg-gradient-to-br from-[#00B3B3] to-[#157DA6]'>
      {/* 
        Flex column layout instead of fixed positioning for the composer.
        This avoids mobile Safari keyboard issues where position:fixed + keyboard = mess.
        See: docs/chat/chat-behaviour-guidelines.md
        
        KEYBOARD FIX: Using CSS dvh (dynamic viewport height) units.
        dvh automatically adjusts when the mobile keyboard appears/disappears,
        providing native browser handling without JavaScript. This is more reliable
        and performant than manually tracking keyboard height via visualViewport.
        
        Browser support: All modern browsers since 2022-2023.
        Fallback: h-screen is used as fallback for older browsers via Tailwind's h-dvh.
      */}
      <motion.div
        ref={mainContainerRef}
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: 1, opacity: 1 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        style={{ transformOrigin: 'bottom' }}
        className='absolute inset-x-0 bottom-0 top-6 mx-auto flex max-w-2xl flex-col overflow-hidden rounded-t-lg bg-surface-bright md:bottom-6 md:left-4 md:right-4 md:rounded-lg'
      >
        {/* Scrollable message area - nested flex pattern for reliable scrolling */}
        <div className='flex min-h-0 flex-1 flex-col'>
          <div
            ref={chatContainerRef}
            className='flex-1 overflow-y-scroll px-4 py-6 scrollbar-gutter-stable'
            style={{ overscrollBehavior: 'contain' }}
          >
            <div className='mx-auto w-full max-w-2xl'>
              {/* Chat transcript */}
              <div className='mb-6 space-y-3 lg:mb-12'>
                {messages.map((m) => {
                  const isPlaceNameMessage = m.id === placeNameMessageId;
                  const isEditingThis = editingPlaceName && isPlaceNameMessage;

                  if (m.from === 'system') {
                    return (
                      <SystemMessage key={m.id}>{m.content}</SystemMessage>
                    );
                  }

                  return (
                    <ChatBubble
                      key={m.id}
                      from={m.from as 'bot' | 'user'}
                      ref={isPlaceNameMessage ? placeNameMessageRef : undefined}
                    >
                      {isEditingThis
                        ? renderPlaceNameContent(editingPlaceName, true)
                        : m.content}
                    </ChatBubble>
                  );
                })}
                <div ref={chatEndRef} />
              </div>

              {/* Address Step */}
              {step === 4 && showAddressSection && (
                <AddressStep
                  ref={addressSectionRef}
                  placeName={placeName}
                  address={address}
                  onAddressChange={setAddress}
                  onNext={handleAddressSubmit}
                  onSkip={handleAddressSubmit}
                  onBack={handleBackToPlaceType}
                  isSubmitting={isSubmitting}
                  isFadingOut={isAddressSectionFadingOut}
                  onFocus={handleAddressFocus}
                />
              )}
            </div>
          </div>
        </div>

        {/* Composer - flex-shrink-0 keeps it at bottom, max-h-[60vh] with overflow-hidden (card selectors handle their own horizontal scroll) */}
        <motion.div
          ref={composerRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className='flex max-h-[60vh] min-h-0 flex-shrink-0 flex-col overflow-hidden rounded-b-lg bg-surface-bright px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-4'
        >
          {step === 1 && (
            <div className='space-y-4'>
              <IndustryStep onSelect={handleIndustrySubmit} />
              <div className='flex items-center'>
                <button
                  type='button'
                  onClick={handleBackToWelcome}
                  className='text-sm text-grey-50 hover:text-grey-70'
                  aria-label='Go back to welcome screen'
                >
                  ← Back
                </button>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className='space-y-3'>
              <PlaceNameStep
                industry={industry}
                placeName={placeName}
                onPlaceNameChange={setPlaceName}
                onSubmit={handlePlaceNameSubmit}
                isMobile={isMobile}
                onFocus={handleInputFocus}
              />
              <div className='flex items-center'>
                <button
                  type='button'
                  onClick={handleBackToIndustry}
                  className='text-sm text-grey-50 hover:text-grey-70'
                  aria-label='Go back to industry selection'
                >
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Place Type Selection */}
          {step === 3 && (
            <div className='space-y-4'>
              <PlaceTypeCardSelector
                industry={industry}
                selectedType={placeType}
                onTypeSelect={handlePlaceTypeSelect}
              />
              <div className='flex items-center'>
                <button
                  type='button'
                  onClick={handleBackToPlaceName}
                  className='text-sm text-grey-50 hover:text-grey-70'
                  aria-label='Go back to place name'
                >
                  ← Back
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Space Selection */}
          {step === 5 && (
            <div className='flex max-h-full min-h-0 flex-col gap-4'>
              {/* Scrollable content area (composer is overflow-hidden + max-height) */}
              <div className='min-h-0 flex-1 overflow-y-auto'>
                {!(isResuming && siteDataFetching) && (
                  <SpaceCardSelector
                    industry={currentIndustry}
                    placeType={siteData?.site?.type || placeType}
                    selectedSpaces={selectedSpaces}
                    customSpaces={customSpaces}
                    onSelectionChange={setSelectedSpaces}
                    onCustomSpacesChange={setCustomSpaces}
                    expandContainerRef={mainContainerRef}
                  />
                )}
                {isResuming && siteDataFetching && (
                  <div className='flex items-center justify-center p-8'>
                    <div className='text-grey-60 text-sm'>
                      Loading space suggestions...
                    </div>
                  </div>
                )}
              </div>

              <div className='flex justify-between gap-2'>
                <button
                  type='button'
                  onClick={handleSpacesSkip}
                  disabled={isSubmitting}
                  className='text-sm text-grey-50 underline hover:text-grey-70 disabled:opacity-50'
                >
                  Skip for now
                </button>
                <button
                  type='button'
                  onClick={handleSpacesSubmit}
                  disabled={selectedSpaces.length === 0 || isSubmitting}
                  className='rounded-md bg-brand px-6 py-2.5 text-white hover:bg-brand-dark disabled:opacity-50'
                >
                  {isSubmitting
                    ? 'Creating...'
                    : `Next (${selectedSpaces.length} selected)`}
                </button>
              </div>
            </div>
          )}

          {/* Step 6: Asset Selection (Multi-select with space assignment) */}
          {step === 6 && (
            <div className='space-y-4'>
              {/* Warning banner when assets need space reassignment */}
              {assetsNeedingSpaceReassignment.length > 0 && (
                <div
                  className='rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800'
                  role='alert'
                >
                  <span className='font-medium'>
                    ⚠️ {assetsNeedingSpaceReassignment.length} asset
                    {assetsNeedingSpaceReassignment.length !== 1
                      ? 's'
                      : ''}{' '}
                    need
                    {assetsNeedingSpaceReassignment.length === 1
                      ? 's'
                      : ''}{' '}
                    space reassignment:
                  </span>{' '}
                  {assetsNeedingSpaceReassignment.map((a) => a.name).join(', ')}
                  . Click on each asset to assign a new space.
                </div>
              )}
              {!(isResuming && siteDataFetching) && (
                <AssetCardSelector
                  industry={currentIndustry}
                  selectedAssets={selectedAssets}
                  onAssetsChange={setSelectedAssets}
                  availableSpaces={selectedSpaces}
                  assetsNeedingReassignment={
                    new Set(assetsNeedingSpaceReassignment.map((a) => a.name))
                  }
                  expandContainerRef={mainContainerRef}
                  onCustomSpaceCreate={(spaceName) => {
                    // Add the custom space to selectedSpaces so it's available for assignment
                    // and will be created in the backend
                    if (!selectedSpaces.includes(spaceName)) {
                      setSelectedSpaces((prev) => [...prev, spaceName]);
                    }
                  }}
                  placeType={siteData?.site?.type || placeType}
                />
              )}
              {isResuming && siteDataFetching && (
                <div className='flex items-center justify-center p-8'>
                  <div className='text-grey-60 text-sm'>
                    Loading asset suggestions...
                  </div>
                </div>
              )}
              <div className='flex items-center justify-between gap-2'>
                <div className='flex items-center gap-4'>
                  <button
                    type='button'
                    onClick={handleBackToSpaces}
                    disabled={isSubmitting}
                    className='text-sm text-grey-50 hover:text-grey-70 disabled:opacity-50'
                    aria-label='Go back to spaces step'
                  >
                    ← Back
                  </button>
                  <button
                    type='button'
                    onClick={handleAssetSkip}
                    disabled={isSubmitting}
                    className='text-sm text-grey-50 underline hover:text-grey-70 disabled:opacity-50'
                  >
                    Skip for now
                  </button>
                </div>
                <button
                  type='button'
                  onClick={handleAssetSubmit}
                  disabled={selectedAssets.length === 0 || isSubmitting}
                  className='rounded-md bg-brand px-6 py-2.5 text-white hover:bg-brand-dark disabled:opacity-50'
                >
                  {(() => {
                    if (isSubmitting) return 'Creating...';
                    const totalCount = selectedAssets.reduce(
                      (sum, a) => sum + a.quantity,
                      0
                    );
                    return `Next (${totalCount} asset${
                      totalCount !== 1 ? 's' : ''
                    })`;
                  })()}
                </button>
              </div>
            </div>
          )}

          {/* Step 7: Recurring Job Setup */}
          {step === 7 && (
            <div className='flex max-h-full min-h-0 flex-col space-y-4'>
              {/* Scrollable content area */}
              <div className='min-h-0 space-y-4 overflow-y-auto'>
                {/* Show loading state when resuming and assets haven't loaded yet */}
                {isResuming && selectedAssets.length === 0 && assetsFetching ? (
                  <div className='flex flex-col items-center justify-center py-8'>
                    <div className='mb-3 h-8 w-8 animate-spin rounded-full border-2 border-brand border-t-transparent' />
                    <div className='text-sm text-grey-50'>
                      Loading your assets...
                    </div>
                  </div>
                ) : selectedAssets.length === 0 ? (
                  /* No assets found - show skip option */
                  <div className='space-y-4'>
                    <div className='text-grey-60 text-sm'>
                      No assets found to set up recurring jobs for.
                    </div>
                    <div className='flex flex-wrap gap-3'>
                      <button
                        type='button'
                        onClick={handleBackToAssets}
                        className='text-sm text-grey-50 hover:text-grey-70'
                      >
                        ← Back to add assets
                      </button>
                      <button
                        type='button'
                        onClick={handleRecurringJobSkip}
                        className='text-sm text-grey-50 underline hover:text-grey-70'
                      >
                        Skip recurring job setup
                      </button>
                    </div>
                  </div>
                ) : (
                  <RecurringJobSetup
                    industry={industry || 'Other Industry'}
                    assets={selectedAssets.map(
                      (a): AssetInfo => ({
                        name: a.name,
                        type: a.type,
                        quantity: a.quantity,
                        spaces: a.spaces,
                        ids: a.ids,
                      })
                    )}
                    onComplete={handleRecurringJobComplete}
                    onSkip={handleRecurringJobSkip}
                    onBack={handleBackToAssets}
                    completedConfigs={recurringJobConfigs}
                    onFinish={() => handleFinishWithJobs(false)}
                    onInputFocus={handleInputFocus}
                    isSubmitting={isSubmitting}
                  />
                )}
              </div>
            </div>
          )}

          {/* Step 8: Finish */}
          {step === 8 && (
            <FinishStep
              onFinish={handleFinishOnboarding}
              showButton={showFinishButton}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Progress bar */}
          <ProgressBar progress={progress} label={progressLabel} />
        </motion.div>
      </motion.div>
    </div>
  );
}

// =============================================================================
// HELPER COMPONENTS
// =============================================================================

function ProgressBar({ progress, label }: { progress: number; label: string }) {
  return (
    <div className='mt-4'>
      <div className='h-1 w-full rounded bg-grey-10'>
        <div
          className='h-1 rounded bg-brand'
          style={{ width: `${progress * 100}%` }}
        />
      </div>
      {label && (
        <div className='mt-1 pb-3 text-center text-xs text-grey-50'>
          {label}
        </div>
      )}
    </div>
  );
}

function SiteCard({
  name,
  addressData,
}: {
  name: string;
  addressData: string | null;
}) {
  return (
    <div className='mx-auto w-full max-w-2xl pt-2'>
      <div className='flex w-fit items-center gap-2 rounded-2xl border border-grey-20 bg-white px-3 py-2'>
        <div className='h-12 w-12 flex-shrink-0'>
          <img
            src={HousePlaceholderImg}
            alt='House'
            className='h-full w-full rounded object-cover'
          />
        </div>
        <div className='flex flex-col'>
          <div className='font-medium'>{name}</div>
          <div className='text-grey-60 text-sm'>
            <Address data={addressData} />
          </div>
        </div>
      </div>
    </div>
  );
}

function JobCard({ title, isSecond }: { title: string; isSecond?: boolean }) {
  return (
    <div
      className={`mx-auto w-full max-w-2xl pt-1 ${isSecond ? '-mt-1' : 'mt-3'}`}
    >
      <div className='mb-2 flex items-center gap-2 rounded border border-grey-20 bg-white px-2 py-2 shadow-sm transition-all duration-200 lg:mb-3 lg:px-4 lg:py-3'>
        <div className='ml-2 mr-3 flex-1 overflow-hidden'>
          <div className='items-center gap-2 md:flex'>
            <div className='mb-0 flex-shrink flex-grow-0 text-sm font-semibold leading-4 text-grey-70 md:text-base lg:max-w-[70%] xl:mb-0'>
              {title}
            </div>
            <div className='mt-1 hidden h-1 w-1 rounded-full bg-grey-90 md:block'></div>
            <div className='secondary-text mb-0 flex-1 flex-grow truncate text-sm text-grey-40 md:mt-0 md:text-base'>
              <span>Scheduled today</span>
            </div>
          </div>
        </div>
        <div className='flex h-full items-center gap-3'>
          <div className='mt-auto flex items-end justify-between md:mt-0 md:items-center lg:items-end xl:mr-1 xl:mt-0 xl:items-center'>
            <StatusBadge value={JobStatus.Accepted} />
          </div>
        </div>
      </div>
    </div>
  );
}
